import { useState, useMemo, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import EmojiPicker from 'emoji-picker-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line, ResponsiveContainer } from "recharts";
import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult, signOut, onAuthStateChanged } from "firebase/auth";
import { getFirestore, collection, doc, addDoc, updateDoc, deleteDoc, setDoc, getDoc, onSnapshot, writeBatch } from "firebase/firestore";

const FIREBASE_CONFIG = {
  apiKey:            "AIzaSyD4-7lF0WNTQgRJ2xV0ALVFBITOijCk0Og",
  authDomain:        "pot-budget.firebaseapp.com",
  projectId:         "pot-budget",
  storageBucket:     "pot-budget.firebasestorage.app",
  messagingSenderId: "292427680707",
  appId:             "1:292427680707:web:32934b5f4d37f8a26f8b76",
  measurementId:     "G-EKBNJ8LQXQ",
};
const ALLOWED_EMAILS = ["ss0421aa@gmail.com","aa053798156@gmail.com","brthday50407@gmail.com","potguozi@gmail.com"];

const firebaseApp = initializeApp(FIREBASE_CONFIG);
const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);

const DARK = {
  bg:"#0F0F13",card:"#1E293B",sub:"#0F0F13",muted:"#64748B",
  border:"#334155",text:"#FFFFFF",textSub:"#CBD5E1",textDim:"#64748B",
  inputBg:"#0F0F13",btnBg:"#334155",btnText:"#94A3B8",
  sidebarBg:"linear-gradient(180deg,#1a1a2e,#0F0F13)",
  headerBg:"linear-gradient(135deg,#1a1a2e,#16213e)",
  modalBase:"#1a1a2e",overlay:"rgba(0,0,0,0.8)",
};
const LIGHT = {
  bg:"#F1F5F9",card:"#FFFFFF",sub:"#F8FAFC",muted:"#64748B",
  border:"#CBD5E1",text:"#1E293B",textSub:"#475569",textDim:"#94A3B8",
  inputBg:"#F8FAFC",btnBg:"#E2E8F0",btnText:"#64748B",
  sidebarBg:"linear-gradient(180deg,#FFFFFF,#F1F5F9)",
  headerBg:"linear-gradient(135deg,#FFFFFF,#F1F5F9)",
  modalBase:"#FFFFFF",overlay:"rgba(0,0,0,0.45)",
};

const BASE_CATEGORIES = [
  { name:"餐飲", icon:"🍜", color:"#FF6B6B", type:"expense" },
  { name:"交通", icon:"🚇", color:"#4ECDC4", type:"expense" },
  { name:"購物", icon:"🛍️", color:"#FFE66D", type:"expense" },
  { name:"娛樂", icon:"🎬", color:"#A78BFA", type:"expense" },
  { name:"醫療", icon:"💊", color:"#FB923C", type:"expense" },
  { name:"住宿", icon:"🏠", color:"#60A5FA", type:"expense" },
  { name:"教育", icon:"📚", color:"#34D399", type:"expense" },
  { name:"其他", icon:"📦", color:"#94A3B8", type:"expense" },
];
const BASE_INCOME_CATEGORIES = [
  { name:"薪水", icon:"💰", color:"#34D399", type:"income" },
  { name:"獎金", icon:"🎁", color:"#FBBF24", type:"income" },
  { name:"投資", icon:"📈", color:"#60A5FA", type:"income" },
  { name:"副業", icon:"💼", color:"#818CF8", type:"income" },
  { name:"利息", icon:"🏦", color:"#10B981", type:"income" },
  { name:"退款", icon:"💸", color:"#FB923C", type:"income" },
];
const TAG_COLORS = ["#F472B6","#60A5FA","#34D399","#FBBF24","#A78BFA","#FB923C","#4ECDC4","#FF6B6B","#94A3B8","#E879F9"];
const PAYMENT_METHODS = [
  { name:"現金",     icon:"💵", color:"#34D399" },
  { name:"信用卡",   icon:"💳", color:"#818CF8" },
  { name:"電子錢包", icon:"📱", color:"#60A5FA" },
  { name:"行動支付", icon:"📲", color:"#FBBF24" },
  { name:"轉帳",    icon:"🏧", color:"#FB923C" },
];
const MONTHS = ["1月","2月","3月","4月","5月","6月","7月","8月","9月","10月","11月","12月"];
const PAYMENT_PRESET_ICONS = ["💵","💳","📱","📲","🏧","💰","💸","🏦","💶","💷","💴","💹"];
const PAYMENT_PRESET_COLORS = ["#34D399","#818CF8","#60A5FA","#FBBF24","#FB923C","#F472B6","#F87171","#4ECDC4","#94A3B8","#E879F9","#10B981","#6366F1"];
const CAT_PRESET_ICONS = ["🍜","🚇","🛍️","🎬","💊","🏠","📚","📦","✈️","🎵","🎯","☕","🎮","💄","🏋️","🐾"];
const CAT_PRESET_COLORS = ["#6366F1","#F472B6","#34D399","#FBBF24","#60A5FA","#FB923C","#A78BFA","#4ECDC4","#FF6B6B","#E879F9","#10B981","#F43F5E"];

const fmt = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
const today = () => fmt(new Date());
const EMPTY_FORM = { type:"expense", amount:"", category:"餐飲", note:"", date:today(), tags:[], payment:"現金" };

function dateLabel(dateStr) {
  const t = fmt(new Date());
  const y = fmt(new Date(Date.now()-86400000));
  if (dateStr===t) return "今天";
  if (dateStr===y) return "昨天";
  const diff = Math.round((new Date(t)-new Date(dateStr))/86400000);
  if (diff===2) return "前天";
  return dateStr;
}
function getWeekKey(dateStr) {
  const d = new Date(dateStr+"T12:00:00");
  const mon = new Date(d); mon.setDate(d.getDate()-((d.getDay()+6)%7));
  const sun = new Date(mon); sun.setDate(mon.getDate()+6);
  return `${fmt(mon)} ~ ${fmt(sun)}`;
}
function weekLabel(key) {
  const [s,e]=key.split(' ~ ');
  const [,sm,sd]=s.split('-'); const [,em,ed]=e.split('-');
  return sm===em?`${parseInt(sm)}/${parseInt(sd)}-${parseInt(ed)}`:`${parseInt(sm)}/${parseInt(sd)}-${parseInt(em)}/${parseInt(ed)}`;
}
function tagColor(tag, allTags) {
  const idx = [...allTags].sort().indexOf(tag);
  return TAG_COLORS[idx % TAG_COLORS.length];
}


function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, position:"relative", zIndex: isDragging ? 1 : "auto" };
  return <div ref={setNodeRef} style={style}>{children({ dragProps: {...attributes, ...listeners} })}</div>;
}
function LoginScreen({ onLogin, loading, error }) {
  return (
    <div style={{fontFamily:"'Noto Sans TC',sans-serif",background:"#0F0F13",minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+TC:wght@300;400;500;700&family=DM+Mono&display=swap" rel="stylesheet"/>
      <div style={{textAlign:"center",maxWidth:320,width:"100%"}}>
        <div style={{fontSize:64,marginBottom:16}}>🔐</div>
        <div style={{fontSize:11,letterSpacing:3,color:"#64748B",marginBottom:8}}>MY BUDGET</div>
        <div style={{fontSize:32,fontWeight:700,marginBottom:8,letterSpacing:-1,color:"#FFFFFF"}}>記帳本</div>
        <div style={{fontSize:14,color:"#64748B",marginBottom:40}}>請登入以繼續</div>
        <button onClick={onLogin} disabled={loading} style={{width:"100%",padding:"14px 20px",border:"none",borderRadius:14,cursor:loading?"not-allowed":"pointer",background:loading?"#334155":"#fff",color:"#1a1a2e",fontSize:15,fontWeight:700,fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:10,opacity:loading?0.6:1,transition:"all 0.2s"}}>
          <svg width="20" height="20" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          {loading ? "登入中..." : "使用 Google 帳號登入"}
        </button>
        {error && <div style={{marginTop:16,padding:"12px 16px",background:"#F8717122",borderRadius:10,fontSize:13,color:"#F87171"}}>{error}</div>}
        <div style={{marginTop:24,fontSize:12,color:"#475569"}}>只有授權帳號可以登入</div>
      </div>
    </div>
  );
}

function UnauthorizedScreen({ email, onLogout }) {
  return (
    <div style={{fontFamily:"'Noto Sans TC',sans-serif",background:"#0F0F13",minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{textAlign:"center",maxWidth:320,width:"100%"}}>
        <div style={{fontSize:64,marginBottom:16}}>🔒</div>
        <div style={{fontSize:20,fontWeight:700,marginBottom:8,color:"#E2E8F0"}}>無法存取</div>
        <div style={{fontSize:13,color:"#64748B",marginBottom:6}}>此帳號未授權</div>
        <div style={{fontSize:13,color:"#F87171",marginBottom:32,background:"#F8717122",padding:"8px 14px",borderRadius:8}}>{email}</div>
        <button onClick={onLogout} style={{padding:"12px 32px",border:"1px solid #334155",borderRadius:12,background:"transparent",color:"#94A3B8",cursor:"pointer",fontSize:14,fontFamily:"inherit"}}>登出</button>
      </div>
    </div>
  );
}

function CalendarPicker({ value, onChange, onClose, T }) {
  const init = value ? new Date(value+"T12:00:00") : new Date();
  const [vY, setVY] = useState(init.getFullYear());
  const [vM, setVM] = useState(init.getMonth());
  const todayStr = fmt(new Date());
  const firstDay = new Date(vY, vM, 1).getDay();
  const daysInMonth = new Date(vY, vM+1, 0).getDate();
  const cells = [];
  for (let i=0; i<(firstDay+6)%7; i++) cells.push(null);
  for (let i=1; i<=daysInMonth; i++) cells.push(i);
  const dayStr = d => `${vY}-${String(vM+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
  const prevM = () => { if(vM===0){setVY(y=>y-1);setVM(11);}else{setVM(m=>m-1);} };
  const nextM = () => { if(vM===11){setVY(y=>y+1);setVM(0);}else{setVM(m=>m+1);} };
  return (
    <div style={{position:"fixed",inset:0,zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,0.7)"}} onClick={onClose}>
      <div style={{background:T.modalBase,borderRadius:16,padding:20,width:300,boxShadow:"0 20px 60px rgba(0,0,0,0.5)"}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
          <button onClick={prevM} style={{background:"none",border:"none",cursor:"pointer",color:T.text,fontSize:20,padding:"4px 8px"}}>{"<"}</button>
          <span style={{fontWeight:700,color:T.text}}>{vY}年{MONTHS[vM]}</span>
          <button onClick={nextM} style={{background:"none",border:"none",cursor:"pointer",color:T.text,fontSize:20,padding:"4px 8px"}}>{">"}</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:8}}>
          {["一","二","三","四","五","六","日"].map(d=>(
            <div key={d} style={{textAlign:"center",fontSize:11,color:T.muted,padding:"4px 0"}}>{d}</div>
          ))}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
          {cells.map((d,i) => {
            if(!d) return <div key={i}/>;
            const ds = dayStr(d);
            const isSelected = ds===value;
            const isToday = ds===todayStr;
            return (
              <button key={i} onClick={()=>{onChange(ds);onClose();}} style={{border:"none",borderRadius:8,padding:"8px 0",cursor:"pointer",fontSize:13,fontWeight:isSelected?700:400,background:isSelected?"#6366F1":isToday?"#6366F122":"transparent",color:isSelected?"#fff":isToday?"#818CF8":T.text}}>
                {d}
              </button>
            );
          })}
        </div>
        <button onClick={()=>{onChange(todayStr);onClose();}} style={{marginTop:12,width:"100%",padding:"10px",border:`1px solid ${T.border}`,borderRadius:10,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13,fontFamily:"inherit"}}>今天</button>
      </div>
    </div>
  );
}

function ConfirmDialog({ message, onConfirm, onCancel, th }) {
  return (
    <div style={{position:"fixed",inset:0,zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",background:th.overlay}} onClick={onCancel}>
      <div style={{background:th.modalBase,borderRadius:16,padding:24,width:280,boxShadow:"0 20px 60px rgba(0,0,0,0.5)"}} onClick={e=>e.stopPropagation()}>
        <div style={{fontSize:16,fontWeight:600,marginBottom:8,color:th.text}}>確認</div>
        <div style={{fontSize:14,color:th.textSub,marginBottom:24}}>{message}</div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={onCancel} style={{flex:1,padding:"12px",border:`1px solid ${th.border}`,borderRadius:12,background:"transparent",color:th.muted,cursor:"pointer",fontSize:14,fontFamily:"inherit"}}>取消</button>
          <button onClick={onConfirm} style={{flex:1,padding:"12px",border:"none",borderRadius:12,background:"linear-gradient(135deg,#F87171,#EF4444)",color:"#fff",cursor:"pointer",fontSize:14,fontWeight:700,fontFamily:"inherit"}}>確認刪除</button>
        </div>
      </div>
    </div>
  );
}

function PaymentMethodsModal({ methods, onAdd, onUpdate, onDelete, onReorder, onClose, T, isDesktop, isIncome }) {
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("💵");
  const [color, setColor] = useState("#60A5FA");
  const [customIcon, setCustomIcon] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editIcon, setEditIcon] = useState("");
  const [editColor, setEditColor] = useState("");
  const [editCustomIcon, setEditCustomIcon] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const startEdit = (m) => { setEditId(m.id); setEditName(m.name); setEditIcon(m.icon); setEditColor(m.color); setEditCustomIcon(""); };
  const cancelEdit = () => { setEditId(null); setEditCustomIcon(""); };
  const confirmEdit = () => { if(editName.trim()){ onUpdate(editId,{name:editName.trim(),icon:editIcon,color:editColor}); setEditId(null); } };
  const pickCustomIcon = (val, setIc, setCust) => { const em=[...val].pop()||""; setCust(em); if(em) setIc(em); };

  const sensors = useSensors(useSensor(PointerSensor,{activationConstraint:{distance:5}}),useSensor(TouchSensor,{activationConstraint:{delay:150,tolerance:5}}));
  const handleMethodDragEnd = ({active,over}) => { if(over&&active.id!==over.id){ const oi=methods.findIndex(m=>m.id===active.id),ni=methods.findIndex(m=>m.id===over.id); onReorder(arrayMove(methods,oi,ni)); } };
  const MethodList = (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleMethodDragEnd}>
      <SortableContext items={methods.filter(m=>m.id&&m.id!==editId).map(m=>m.id)} strategy={verticalListSortingStrategy}>
        <div style={{flex:1,overflowY:"auto"}}>
          {methods.map(m=>(
            editId===m.id ? (
              <div key={m.id||m.name} style={{padding:"12px",background:T.card,borderRadius:10,marginBottom:6,border:`1px solid ${editColor}`}}>
                <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:6}}>
                  {PAYMENT_PRESET_ICONS.map(ic=>(
                    <button key={ic} onClick={()=>{setEditIcon(ic);setEditCustomIcon("");}} style={{width:30,height:30,border:`2px solid ${ic===editIcon?editColor:"transparent"}`,borderRadius:8,background:ic===editIcon?editColor+"22":"transparent",cursor:"pointer",fontSize:15}}>{ic}</button>
                  ))}
                </div>
                <input value={editCustomIcon} onChange={e=>pickCustomIcon(e.target.value,setEditIcon,setEditCustomIcon)} placeholder="輸入 emoji" style={{width:"100%",padding:"5px 10px",border:`1px solid ${T.border}`,borderRadius:8,background:T.inputBg,color:T.text,fontSize:14,fontFamily:"inherit",boxSizing:"border-box",marginBottom:6}}/>
                <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:8}}>
                  {PAYMENT_PRESET_COLORS.map(c=>(
                    <button key={c} onClick={()=>setEditColor(c)} style={{width:22,height:22,borderRadius:"50%",background:c,border:`3px solid ${c===editColor?"#fff":"transparent"}`,cursor:"pointer",outline:c===editColor?"2px solid "+c:"none",outlineOffset:2}}/>
                  ))}
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <div style={{width:30,height:30,borderRadius:8,background:editColor+"22",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>{editIcon}</div>
                  <input value={editName} onChange={e=>setEditName(e.target.value)} style={{flex:1,padding:"6px 10px",border:`1px solid ${T.border}`,borderRadius:8,background:T.inputBg,color:T.text,fontSize:14,fontFamily:"inherit"}}/>
                  <button onClick={confirmEdit} disabled={!editName.trim()} style={{padding:"6px 12px",border:"none",borderRadius:8,background:editName.trim()?"#6366F1":"#334155",color:"#fff",cursor:editName.trim()?"pointer":"not-allowed",fontSize:12,fontFamily:"inherit"}}>儲存</button>
                  <button onClick={cancelEdit} style={{padding:"6px 10px",border:`1px solid ${T.border}`,borderRadius:8,background:"transparent",color:T.muted,cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>取消</button>
                </div>
              </div>
            ) : (
              <SortableItem key={m.id||m.name} id={m.id||m.name}>{({dragProps})=>(
                <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:T.card,borderRadius:10,marginBottom:6,border:`1px solid ${T.border}`}}>
                  {m.id&&<span {...dragProps} style={{cursor:"grab",color:T.muted,fontSize:16,flexShrink:0,touchAction:"none",padding:"0 4px",userSelect:"none"}}>≡</span>}
                  <span style={{fontSize:18}}>{m.icon}</span>
                  <span style={{flex:1,fontSize:14,color:T.text}}>{m.name}</span>
                  <div style={{width:12,height:12,borderRadius:"50%",background:m.color,flexShrink:0}}/>
                  {m.id&&<button onClick={()=>startEdit(m)} style={{width:28,height:28,borderRadius:7,background:T.btnBg,border:"none",cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>✏️</button>}
                  {m.id&&<button onClick={()=>onDelete(m.id)} style={{width:28,height:28,borderRadius:7,background:"#F8717122",border:"none",cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>🗑️</button>}
                </div>
              )}</SortableItem>
            )
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );

  const AddForm = (
    <div style={isDesktop?{width:300,flexShrink:0,borderLeft:`1px solid ${T.border}`,paddingLeft:24}:{borderTop:`1px solid ${T.border}`,paddingTop:16,marginTop:4}}>
      {isDesktop&&<div style={{fontSize:13,fontWeight:600,color:T.text,marginBottom:16}}>{isIncome?"收款方式管理":"付款方式管理"}</div>}
      {!isDesktop&&<div style={{fontSize:12,color:T.muted,marginBottom:12}}>{isIncome?"收款方式管理":"付款方式管理"}</div>}
      <div style={{fontSize:11,color:T.muted,letterSpacing:2,marginBottom:6}}>圖示</div>
      <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:6}}>
        {PAYMENT_PRESET_ICONS.map(ic=>(
          <button key={ic} onClick={()=>{setIcon(ic);setCustomIcon("");}} style={{width:34,height:34,border:`2px solid ${ic===icon?color:"transparent"}`,borderRadius:8,background:ic===icon?color+"22":"transparent",cursor:"pointer",fontSize:17}}>{ic}</button>
        ))}
      </div>
      <button onClick={()=>setShowEmojiPicker(true)} style={{width:"100%",padding:"8px",border:`1px solid ${T.border}`,borderRadius:8,background:T.inputBg,color:T.text,fontSize:13,fontFamily:"inherit",cursor:"pointer",marginBottom:10,textAlign:"left"}}>😊 更多 emoji…</button>
      <div style={{fontSize:11,color:T.muted,letterSpacing:2,marginBottom:6}}>顏色</div>
      <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:12,paddingLeft:4}}>
        {PAYMENT_PRESET_COLORS.map(c=>(
          <button key={c} onClick={()=>setColor(c)} style={{width:26,height:26,borderRadius:"50%",background:c,border:`3px solid ${c===color?"#fff":"transparent"}`,cursor:"pointer",outline:c===color?"2px solid "+c:"none",outlineOffset:2}}/>
        ))}
      </div>
      <div style={{fontSize:11,color:T.muted,letterSpacing:2,marginBottom:6}}>名稱</div>
      <div style={{display:"flex",gap:8,marginBottom:8}}>
        <div style={{width:34,height:34,borderRadius:10,background:color+"22",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{icon}</div>
        <input value={name} onChange={e=>setName(e.target.value)} placeholder="輸入名稱" style={{flex:1,padding:"7px 12px",border:`1px solid ${T.border}`,borderRadius:10,background:T.inputBg,color:T.text,fontSize:14,fontFamily:"inherit",outline:"none"}}/>
      </div>
      <button onClick={()=>{ if(name.trim()){onAdd({name:name.trim(),icon,color});setName("");setCustomIcon("");setIcon("💵");} }} disabled={!name.trim()} style={{width:"100%",padding:"10px",border:"none",borderRadius:10,background:name.trim()?"#6366F1":"#334155",color:"#fff",cursor:name.trim()?"pointer":"not-allowed",fontSize:14,fontWeight:700,fontFamily:"inherit"}}>新增</button>
    </div>
  );

  return (
    <div style={{position:"fixed",inset:0,zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",background:T.overlay}} onClick={onClose}>
      <div data-tour="cat-manage-modal" style={{background:T.modalBase,borderRadius:16,padding:24,width:isDesktop?"min(840px,66vw)":"87.5vw",maxHeight:"85vh",display:"flex",flexDirection:"column",boxShadow:"0 20px 60px rgba(0,0,0,0.5)"}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexShrink:0}}>
          <div style={{fontSize:16,fontWeight:700,color:T.text}}>{isIncome?"收款方式管理":"付款方式管理"}</div>
          <button onClick={onClose} style={{background:T.btnBg,border:"none",borderRadius:8,width:32,height:32,cursor:"pointer",color:T.btnText,fontSize:18,display:"flex",alignItems:"center",justifyContent:"center"}}>x</button>
        </div>
      {isDesktop ? (
          <div style={{display:"flex",gap:0,minHeight:0,flex:1,overflow:"hidden"}}>
            <div style={{flex:1,overflowY:"auto",paddingRight:24}}>
              <div style={{fontSize:13,fontWeight:600,color:T.text,marginBottom:12}}>{isIncome?"已設收款方式":"已設付款方式"}</div>
              {MethodList}
            </div>
            {AddForm}
          </div>
        ) : (
          <div style={{flex:1,minHeight:0,overflowY:"auto"}}>
            {MethodList}
            {AddForm}
          </div>
        )}
      </div>
    {showEmojiPicker&&(
      <div style={{position:"fixed",inset:0,zIndex:500,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={e=>{e.stopPropagation();setShowEmojiPicker(false);}}>
        <div onClick={e=>e.stopPropagation()}>
          <EmojiPicker onEmojiClick={d=>{setIcon(d.emoji);setShowEmojiPicker(false);}} theme="auto" lazyLoadEmojis={true}/>
        </div>
      </div>
    )}
    </div>
  );
}

function ManageCategoriesModal({ categories, onAdd, onUpdate, onDelete, onReorder, onClose, T, isDesktop }) {
  const [catType, setCatType] = useState("expense");
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("🍜");
  const [color, setColor] = useState("#6366F1");
  const [customIcon, setCustomIcon] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editIcon, setEditIcon] = useState("");
  const [editColor, setEditColor] = useState("");
  const [editCustomIcon, setEditCustomIcon] = useState("");
  const [showEmojiPickerCat, setShowEmojiPickerCat] = useState(false);

  const startEdit = (c) => { setEditId(c.id); setEditName(c.name); setEditIcon(c.icon); setEditColor(c.color); setEditCustomIcon(""); };
  const cancelEdit = () => { setEditId(null); setEditCustomIcon(""); };
  const confirmEdit = () => { if(editName.trim()){ onUpdate(editId,{name:editName.trim(),icon:editIcon,color:editColor}); setEditId(null); } };
  const pickCustomIcon = (val, setIc, setCust) => { const em=[...val].pop()||""; setCust(em); if(em) setIc(em); };

  const AddForm = (
    <div style={isDesktop?{width:300,flexShrink:0,borderLeft:`1px solid ${T.border}`,paddingLeft:24}:{borderTop:`1px solid ${T.border}`,paddingTop:16,marginTop:4}}>
      {isDesktop&&<div style={{fontSize:13,fontWeight:600,color:T.text,marginBottom:16}}>新增類別</div>}
      {!isDesktop&&<div style={{fontSize:12,color:T.muted,marginBottom:12}}>新增類別</div>}
      <div style={{fontSize:11,color:T.muted,letterSpacing:2,marginBottom:6}}>圖示</div>
      <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:6}}>
        {CAT_PRESET_ICONS.map(ic=>(
          <button key={ic} onClick={()=>{setIcon(ic);setCustomIcon("");}} style={{width:34,height:34,border:`2px solid ${ic===icon?color:"transparent"}`,borderRadius:8,background:ic===icon?color+"22":"transparent",cursor:"pointer",fontSize:17}}>{ic}</button>
        ))}
      </div>
      <button onClick={()=>setShowEmojiPickerCat(true)} style={{width:"100%",padding:"8px",border:`1px solid ${T.border}`,borderRadius:8,background:T.inputBg,color:T.text,fontSize:13,fontFamily:"inherit",cursor:"pointer",marginBottom:10,textAlign:"left"}}>😊 更多 emoji…</button>
      <div style={{fontSize:11,color:T.muted,letterSpacing:2,marginBottom:6}}>顏色</div>
      <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:12,paddingLeft:4}}>
        {CAT_PRESET_COLORS.map(c=>(
          <button key={c} onClick={()=>setColor(c)} style={{width:26,height:26,borderRadius:"50%",background:c,border:`3px solid ${c===color?"#fff":"transparent"}`,cursor:"pointer",outline:c===color?"2px solid "+c:"none",outlineOffset:2}}/>
        ))}
      </div>
      <div style={{fontSize:11,color:T.muted,letterSpacing:2,marginBottom:6}}>名稱</div>
      <div style={{display:"flex",gap:8,marginBottom:8}}>
        <div style={{width:34,height:34,borderRadius:10,background:color,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{icon}</div>
        <input value={name} onChange={e=>setName(e.target.value)} placeholder="輸入名稱" style={{flex:1,padding:"7px 12px",border:`1px solid ${T.border}`,borderRadius:10,background:T.inputBg,color:T.text,fontSize:14,fontFamily:"inherit",outline:"none"}}/>
      </div>
      <button onClick={()=>{ if(name.trim()){onAdd({name:name.trim(),icon,color,type:catType});setName("");setCustomIcon("");setIcon("🍜");} }} disabled={!name.trim()} style={{width:"100%",padding:"10px",border:"none",borderRadius:10,background:name.trim()?"#6366F1":"#334155",color:"#fff",cursor:name.trim()?"pointer":"not-allowed",fontSize:14,fontWeight:700,fontFamily:"inherit"}}>新增</button>
    </div>
  );

  const filteredCats = categories.filter(c=>catType==="income"?c.type==="income":c.type!=="income");
  const catSensors = useSensors(useSensor(PointerSensor,{activationConstraint:{distance:5}}),useSensor(TouchSensor,{activationConstraint:{delay:150,tolerance:5}}));
  const handleCatDragEnd = ({active,over}) => { if(over&&active.id!==over.id){ const oi=filteredCats.findIndex(c=>c.id===active.id),ni=filteredCats.findIndex(c=>c.id===over.id); onReorder(arrayMove(filteredCats,oi,ni)); } };
  const CategoryList = (
    <DndContext sensors={catSensors} collisionDetection={closestCenter} onDragEnd={handleCatDragEnd}>
      <SortableContext items={filteredCats.filter(c=>c.id&&c.id!==editId).map(c=>c.id)} strategy={verticalListSortingStrategy}>
        {filteredCats.map(c=>(
          editId===c.id ? (
            <div key={c.id||c.name} style={{padding:"12px",background:T.card,borderRadius:10,marginBottom:6,border:`1px solid ${editColor}`}}>
              <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:6}}>
                {CAT_PRESET_ICONS.map(ic=>(
                  <button key={ic} onClick={()=>{setEditIcon(ic);setEditCustomIcon("");}} style={{width:30,height:30,border:`2px solid ${ic===editIcon?editColor:"transparent"}`,borderRadius:8,background:ic===editIcon?editColor+"22":"transparent",cursor:"pointer",fontSize:15}}>{ic}</button>
                ))}
              </div>
              <input value={editCustomIcon} onChange={e=>pickCustomIcon(e.target.value,setEditIcon,setEditCustomIcon)} placeholder="輸入 emoji" style={{width:"100%",padding:"5px 10px",border:`1px solid ${T.border}`,borderRadius:8,background:T.inputBg,color:T.text,fontSize:14,fontFamily:"inherit",boxSizing:"border-box",marginBottom:6}}/>
              <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:8}}>
                {CAT_PRESET_COLORS.map(c=>(
                  <button key={c} onClick={()=>setEditColor(c)} style={{width:22,height:22,borderRadius:"50%",background:c,border:`3px solid ${c===editColor?"#fff":"transparent"}`,cursor:"pointer",outline:c===editColor?"2px solid "+c:"none",outlineOffset:2}}/>
                ))}
              </div>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <div style={{width:30,height:30,borderRadius:8,background:editColor,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>{editIcon}</div>
                <input value={editName} onChange={e=>setEditName(e.target.value)} style={{flex:1,padding:"6px 10px",border:`1px solid ${T.border}`,borderRadius:8,background:T.inputBg,color:T.text,fontSize:14,fontFamily:"inherit"}}/>
                <button onClick={confirmEdit} disabled={!editName.trim()} style={{padding:"6px 12px",border:"none",borderRadius:8,background:editName.trim()?"#6366F1":"#334155",color:"#fff",cursor:editName.trim()?"pointer":"not-allowed",fontSize:12,fontFamily:"inherit"}}>儲存</button>
                <button onClick={cancelEdit} style={{padding:"6px 10px",border:`1px solid ${T.border}`,borderRadius:8,background:"transparent",color:T.muted,cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>取消</button>
              </div>
            </div>
          ) : (
            <SortableItem key={c.id||c.name} id={c.id||c.name}>{({dragProps})=>(
              <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:T.card,borderRadius:10,marginBottom:6,border:`1px solid ${T.border}`}}>
                <span {...dragProps} style={{cursor:"grab",color:T.muted,fontSize:16,flexShrink:0,touchAction:"none",padding:"0 4px",userSelect:"none"}}>≡</span>
                <div style={{width:28,height:28,borderRadius:8,background:c.color,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>{c.icon}</div>
                <span style={{flex:1,fontSize:14,color:T.text}}>{c.name}</span>
                <button onClick={()=>startEdit(c)} style={{width:28,height:28,borderRadius:7,background:T.btnBg,border:"none",cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>✏️</button>
                <button onClick={()=>onDelete(c.id)} style={{width:28,height:28,borderRadius:7,background:"#F8717122",border:"none",cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>🗑️</button>
              </div>
            )}</SortableItem>
          )
        ))}
      </SortableContext>
    </DndContext>
  );

  return (
    <div style={{position:"fixed",inset:0,zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",background:T.overlay}} onClick={onClose}>
      <div style={{background:T.modalBase,borderRadius:16,padding:24,width:isDesktop?"min(840px,66vw)":"87.5vw",maxHeight:"85vh",display:"flex",flexDirection:"column",boxShadow:"0 20px 60px rgba(0,0,0,0.5)"}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexShrink:0}}>
          <div style={{fontSize:16,fontWeight:700,color:T.text}}>類別管理</div>
          <button onClick={onClose} style={{background:T.btnBg,border:"none",borderRadius:8,width:32,height:32,cursor:"pointer",color:T.btnText,fontSize:18,display:"flex",alignItems:"center",justifyContent:"center"}}>x</button>
        </div>
        <div style={{display:"flex",borderRadius:10,overflow:"hidden",border:`1px solid ${T.border}`,marginBottom:16,flexShrink:0}}>
          {[["expense","支出類別"],["income","收入類別"]].map(([v,l])=>(
            <button key={v} onClick={()=>{setCatType(v);setEditId(null);}} style={{flex:1,padding:"8px",border:"none",background:catType===v?(v==="expense"?"#F87171":"#34D399"):T.card,color:catType===v?"#fff":T.muted,cursor:"pointer",fontSize:13,fontWeight:catType===v?700:400,fontFamily:"inherit",transition:"all 0.2s"}}>{l}</button>
          ))}
        </div>
        {isDesktop ? (
          <div style={{display:"flex",gap:0,minHeight:0,flex:1,overflow:"hidden"}}>
            <div style={{flex:1,overflowY:"auto",paddingRight:24}}>
              <div style={{fontSize:13,fontWeight:600,color:T.text,marginBottom:12}}>已設類別</div>
              {CategoryList}
            </div>
            {AddForm}
          </div>
        ) : (
          <div style={{flex:1,minHeight:0,overflowY:"auto"}}>
            {CategoryList}
            {AddForm}
          </div>
        )}
      </div>
    {showEmojiPickerCat&&(
      <div style={{position:"fixed",inset:0,zIndex:500,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={e=>{e.stopPropagation();setShowEmojiPickerCat(false);}}>
        <div onClick={e=>e.stopPropagation()}>
          <EmojiPicker onEmojiClick={d=>{setIcon(d.emoji);setShowEmojiPickerCat(false);}} theme="auto" lazyLoadEmojis={true}/>
        </div>
      </div>
    )}
    </div>
  );
}

function RecurringModal({ record, allCategories, tagDocs, onSave, onClose, T }) {
  const isEdit = !!record;
  const expCats = allCategories.filter(c => c.type !== "income");
  const incCats = allCategories.filter(c => c.type === "income");
  const EMPTY = { name:"", amount:"", day:1, type:"expense", category: expCats[0]?.name||"", note:"", tags:[] };
  const [form, setForm] = useState(record ? {...record, type:record.type||"expense", tags:record.tags||[], amount:String(record.amount)} : EMPTY);
  const [tagInput, setTagInput] = useState("");
  const set = k => v => setForm(f=>({...f,[k]:v}));
  const typeCats = form.type === "income" ? incCats : expCats;
  const catTags = (tagDocs||[]).filter(t=>t.categoryId===form.category).map(t=>t.name);
  const suggestions = catTags.filter(t=>t.toLowerCase().includes(tagInput.toLowerCase())&&!form.tags.includes(t));
  const addTag = (name) => { const t=name.trim().replace(/^#+/,''); if(t&&!form.tags.includes(t)) setForm(f=>({...f,tags:[...f.tags,t]})); setTagInput(""); };
  return (
    <div style={{position:"fixed",inset:0,zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",background:T.overlay}} onClick={onClose}>
      <div style={{background:T.modalBase,borderRadius:16,padding:24,width:"min(540px,92vw)",maxHeight:"85vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.5)"}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div style={{fontSize:18,fontWeight:700,color:T.text}}>{isEdit?"編輯定期":"新增定期"}</div>
          <button onClick={onClose} style={{background:T.btnBg,border:"none",borderRadius:8,width:32,height:32,cursor:"pointer",color:T.btnText,fontSize:18,display:"flex",alignItems:"center",justifyContent:"center"}}>x</button>
        </div>
        <div style={{display:"flex",borderRadius:10,overflow:"hidden",border:`1px solid ${T.border}`,marginBottom:16}}>
          {[["expense","定期支出"],["income","定期收入"]].map(([val,label])=>(
            <button key={val} onClick={()=>{ const cats=val==="income"?incCats:expCats; setForm(f=>({...f,type:val,category:cats[0]?.name||f.category,tags:[]})); }} style={{flex:1,padding:"10px",border:"none",background:form.type===val?(val==="expense"?"#F8717133":"#34D39933"):T.card,color:form.type===val?(val==="expense"?"#F87171":"#34D399"):T.muted,cursor:"pointer",fontSize:13,fontWeight:form.type===val?700:400,fontFamily:"inherit"}}>{label}</button>
          ))}
        </div>
        <div style={{fontSize:11,color:T.muted,letterSpacing:2,marginBottom:6}}>名稱</div>
        <input placeholder="例：Netflix、水電費" value={form.name} onChange={e=>set("name")(e.target.value)} style={{width:"100%",padding:"10px 12px",border:`1px solid ${T.border}`,borderRadius:10,background:T.inputBg,color:T.text,fontSize:14,fontFamily:"inherit",boxSizing:"border-box",marginBottom:12}}/>
        <div style={{fontSize:11,color:T.muted,letterSpacing:2,marginBottom:6}}>金額</div>
        <input type="number" placeholder="0" value={form.amount} onChange={e=>set("amount")(e.target.value)} style={{width:"100%",padding:"10px 12px",border:`1px solid ${T.border}`,borderRadius:10,background:T.inputBg,color:T.text,fontSize:14,fontFamily:"inherit",boxSizing:"border-box",marginBottom:12}}/>
        <div style={{fontSize:11,color:T.muted,letterSpacing:2,marginBottom:6}}>每月{form.type==="income"?"入帳":"扣款"}</div>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
          <input type="number" min="1" max="31" value={form.day} onChange={e=>set("day")(parseInt(e.target.value)||1)} style={{width:80,padding:"10px 12px",border:`1px solid ${T.border}`,borderRadius:10,background:T.inputBg,color:T.text,fontSize:14,fontFamily:"inherit",boxSizing:"border-box"}}/>
          <span style={{fontSize:12,color:"#60A5FA"}}>每月 {form.day} 號{form.type==="income"?"入帳":"扣款"}</span>
        </div>
        <div style={{fontSize:11,color:T.muted,letterSpacing:2,marginBottom:6}}>類別</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:12}}>
          {typeCats.map(c=>(
            <button key={c.name} onClick={()=>set("category")(c.name)} style={{padding:"6px 12px",border:`1px solid ${form.category===c.name?c.color:T.border}`,borderRadius:8,background:form.category===c.name?c.color+"22":"transparent",color:form.category===c.name?c.color:T.muted,cursor:"pointer",fontSize:13,fontFamily:"inherit"}}>
              {c.icon} {c.name}
            </button>
          ))}
        </div>
        <div style={{fontSize:11,color:T.muted,letterSpacing:2,marginBottom:8}}>標籤</div>
        {form.tags.length>0&&(
          <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:8}}>
            {form.tags.map(t=>(
              <span key={t} style={{display:"flex",alignItems:"center",gap:4,padding:"4px 10px",background:T.btnBg,borderRadius:20,fontSize:13,color:T.text}}>
                #{t}
                <button onClick={()=>setForm(f=>({...f,tags:f.tags.filter(x=>x!==t)}))} style={{background:"none",border:"none",cursor:"pointer",color:T.muted,padding:0,fontSize:14,lineHeight:1}}>x</button>
              </span>
            ))}
          </div>
        )}
        <div style={{position:"relative",marginBottom:8}}>
          <div style={{display:"flex",gap:6}}>
            <input placeholder="輸入標籤，Enter 新增" value={tagInput} onChange={e=>setTagInput(e.target.value)} onKeyDown={e=>{ if(e.key==="Enter"&&tagInput.trim()){e.preventDefault();addTag(tagInput);} }} style={{flex:1,padding:"8px 12px",border:`1px solid ${T.border}`,borderRadius:10,background:T.inputBg,color:T.text,fontSize:13,fontFamily:"inherit",outline:"none"}}/>
            <button onClick={()=>addTag(tagInput)} disabled={!tagInput.trim()} style={{padding:"8px 14px",border:"none",borderRadius:10,background:tagInput.trim()?"#6366F1":"#334155",color:"#fff",cursor:tagInput.trim()?"pointer":"not-allowed",fontSize:13,fontFamily:"inherit"}}>+</button>
          </div>
          {suggestions.length>0&&tagInput&&(
            <div style={{position:"absolute",top:"100%",left:0,right:0,background:T.card,border:`1px solid ${T.border}`,borderRadius:10,marginTop:4,zIndex:10,overflow:"hidden"}}>
              {suggestions.slice(0,5).map(s=>(
                <button key={s} onClick={()=>addTag(s)} style={{display:"block",width:"100%",padding:"8px 12px",border:"none",background:"transparent",color:T.text,cursor:"pointer",fontSize:13,fontFamily:"inherit",textAlign:"left"}}>#{s}</button>
              ))}
            </div>
          )}
        </div>
        {(()=>{ const avail=catTags.filter(t=>!form.tags.includes(t)); return avail.length>0&&(
          <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:12}}>
            {avail.map(t=>(
              <button key={t} onClick={()=>addTag(t)} style={{padding:"4px 10px",border:`1px solid ${T.border}`,borderRadius:20,background:"transparent",color:T.textSub,cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>#{t}</button>
            ))}
          </div>
        ); })()}
        <div style={{fontSize:11,color:T.muted,letterSpacing:2,marginBottom:6}}>備註</div>
        <input placeholder="備註（選填）" value={form.note} onChange={e=>set("note")(e.target.value)} style={{width:"100%",padding:"10px 12px",border:`1px solid ${T.border}`,borderRadius:10,background:T.inputBg,color:T.text,fontSize:14,fontFamily:"inherit",boxSizing:"border-box",marginBottom:20}}/>
        <button onClick={()=>{ if(form.name&&form.amount) onSave({...form,amount:parseFloat(form.amount),day:parseInt(form.day)||1}); }} disabled={!form.name||!form.amount} style={{width:"100%",padding:"12px",border:"none",borderRadius:12,background:(form.name&&form.amount)?"linear-gradient(135deg,#6366F1,#8B5CF6)":"#334155",color:"#fff",cursor:(form.name&&form.amount)?"pointer":"not-allowed",fontSize:15,fontWeight:700,fontFamily:"inherit"}}>
          {isEdit ? "儲存變更" : form.type==="income"?"新增定期收入":"新增定期支出"}
        </button>
      </div>
    </div>
  );
}

function RecordModal({ record, allCategories, tagDocs, allPaymentMethods, onSave, onClose, T }) {
  const isEdit = !!record;
  const [form, setForm] = useState(record ? {...record, amount:String(record.amount)} : {...EMPTY_FORM});
  const [tagInput, setTagInput] = useState("");
  const [showCal, setShowCal] = useState(false);
  const tagRef = useRef(null);
  const set = k => v => setForm(f=>({...f,[k]:v}));

  const formCats = useMemo(()=>allCategories.filter(c=>form.type==="income"?c.type==="income":c.type!=="income"),[allCategories,form.type]);

  const catTags = useMemo(()=>
    tagDocs.filter(t=>t.categoryId===form.category).map(t=>t.name),
    [tagDocs, form.category]
  );
  const suggestions = catTags.filter(t=>t.toLowerCase().includes(tagInput.toLowerCase())&&!form.tags.includes(t));

  const addTag = (name) => {
    const t = name.trim().replace(/^#+/,'');
    if (t && !form.tags.includes(t)) setForm(f=>({...f,tags:[...f.tags,t]}));
    setTagInput("");
  };

  const cat = formCats.find(c=>c.name===form.category)||formCats[0]||allCategories[0];

  return (
    <div style={{position:"fixed",inset:0,zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",background:T.overlay}} onClick={onClose}>
      {showCal&&<CalendarPicker value={form.date} onChange={v=>set("date")(v)} onClose={()=>setShowCal(false)} T={T}/>}
      <div style={{background:T.modalBase,borderRadius:16,padding:24,width:340,maxHeight:"90vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.5)"}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div style={{fontSize:18,fontWeight:700,color:T.text}}>{isEdit?"編輯記錄":"新增記錄"}</div>
          <button onClick={onClose} style={{background:T.btnBg,border:"none",borderRadius:8,width:32,height:32,cursor:"pointer",color:T.btnText,fontSize:18,display:"flex",alignItems:"center",justifyContent:"center"}}>x</button>
        </div>
        <div style={{display:"flex",borderRadius:12,overflow:"hidden",border:`1px solid ${T.border}`,marginBottom:16}}>
          {[["expense","支出"],["income","收入"]].map(([val,label])=>(
            <button key={val} onClick={()=>{ const cats=allCategories.filter(c=>val==="income"?c.type==="income":c.type!=="income"); setForm(f=>({...f,type:val,tags:[],category:cats[0]?.name||f.category})); }} style={{flex:1,padding:"10px",border:"none",background:form.type===val?(val==="expense"?"#F87171":"#34D399"):T.card,color:form.type===val?"#fff":T.muted,cursor:"pointer",fontSize:14,fontWeight:form.type===val?700:400,fontFamily:"inherit"}}>{label}</button>
          ))}
        </div>
        <div style={{fontSize:11,color:T.muted,letterSpacing:2,marginBottom:6}}>金額</div>
        <div style={{display:"flex",alignItems:"center",border:`1px solid ${T.border}`,borderRadius:10,background:T.inputBg,marginBottom:16,overflow:"hidden"}}>
          <span style={{padding:"0 12px",color:T.muted,fontSize:16}}>$</span>
          <input type="number" min="0" placeholder="0" value={form.amount} onChange={e=>set("amount")(e.target.value)} style={{flex:1,padding:"12px 12px 12px 0",border:"none",background:"transparent",color:T.text,fontSize:18,fontFamily:"'DM Mono',monospace",outline:"none"}}/>
        </div>
        <div style={{fontSize:11,color:T.muted,letterSpacing:2,marginBottom:8}}>類別</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:16}}>
          {formCats.map(c=>(
            <button key={c.name} onClick={()=>setForm(f=>({...f,category:c.name,tags:[]}))} style={{padding:"6px 12px",border:`1px solid ${form.category===c.name?c.color:T.border}`,borderRadius:8,background:form.category===c.name?c.color+"22":"transparent",color:form.category===c.name?c.color:T.muted,cursor:"pointer",fontSize:13,fontFamily:"inherit"}}>
              {c.icon} {c.name}
            </button>
          ))}
        </div>
        <div style={{fontSize:11,color:T.muted,letterSpacing:2,marginBottom:8}}>
          標籤 <span style={{color:T.textDim,fontSize:10,letterSpacing:0}}>（{cat.icon} {form.category} 的標籤）</span>
        </div>
        {form.tags.length>0&&(
          <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:8}}>
            {form.tags.map(t=>(
              <span key={t} style={{display:"flex",alignItems:"center",gap:4,padding:"4px 10px",background:T.btnBg,borderRadius:20,fontSize:13,color:T.text}}>
                #{t}
                <button onClick={()=>setForm(f=>({...f,tags:f.tags.filter(x=>x!==t)}))} style={{background:"none",border:"none",cursor:"pointer",color:T.muted,padding:0,fontSize:14,lineHeight:1}}>x</button>
              </span>
            ))}
          </div>
        )}
        <div style={{position:"relative",marginBottom:8}}>
          <div style={{display:"flex",gap:6}}>
            <input ref={tagRef} placeholder="輸入標籤，Enter 新增" value={tagInput} onChange={e=>setTagInput(e.target.value)} onKeyDown={e=>{ if(e.key==="Enter"&&tagInput.trim()){e.preventDefault();addTag(tagInput);} }} style={{flex:1,padding:"8px 12px",border:`1px solid ${T.border}`,borderRadius:10,background:T.inputBg,color:T.text,fontSize:13,fontFamily:"inherit",outline:"none"}}/>
            <button onClick={()=>addTag(tagInput)} disabled={!tagInput.trim()} style={{padding:"8px 14px",border:"none",borderRadius:10,background:tagInput.trim()?"#6366F1":"#334155",color:"#fff",cursor:tagInput.trim()?"pointer":"not-allowed",fontSize:13,fontFamily:"inherit"}}>+</button>
          </div>
          {suggestions.length>0&&tagInput&&(
            <div style={{position:"absolute",top:"100%",left:0,right:0,background:T.card,border:`1px solid ${T.border}`,borderRadius:10,marginTop:4,zIndex:10,overflow:"hidden"}}>
              {suggestions.slice(0,5).map(s=>(
                <button key={s} onClick={()=>addTag(s)} style={{display:"block",width:"100%",padding:"8px 12px",border:"none",background:"transparent",color:T.text,cursor:"pointer",fontSize:13,fontFamily:"inherit",textAlign:"left"}}>#{s}</button>
              ))}
            </div>
          )}
        </div>
        {(()=>{ const avail=catTags.filter(t=>!form.tags.includes(t)); return avail.length>0&&(
          <div style={{marginBottom:12}}>
            <div style={{fontSize:11,color:T.textDim,marginBottom:6}}>快速新增：{cat.icon} {form.category} 的標籤</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {avail.map(t=>(
                <button key={t} onClick={()=>addTag(t)} style={{padding:"4px 10px",border:`1px solid ${T.border}`,borderRadius:20,background:"transparent",color:T.textSub,cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>#{t}</button>
              ))}
            </div>
          </div>
        ); })()}
        <div style={{fontSize:11,color:T.muted,letterSpacing:2,marginBottom:6}}>備註</div>
        <input placeholder="備註（選填）" value={form.note} onChange={e=>set("note")(e.target.value)} style={{width:"100%",padding:"10px 12px",border:`1px solid ${T.border}`,borderRadius:10,background:T.inputBg,color:T.text,fontSize:14,fontFamily:"inherit",boxSizing:"border-box",marginBottom:12}}/>
        <div style={{fontSize:11,color:T.muted,letterSpacing:2,marginBottom:8}}>{form.type==="income"?"付款方式":"付款方式"}</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:12}}>
          {allPaymentMethods.map(p=>{ const sel=(form.payment||"現金")===p.name; return (
            <button key={p.name} onClick={()=>set("payment")(p.name)} style={{padding:"7px 12px",border:`1px solid ${sel?p.color:T.border}`,borderRadius:10,background:sel?p.color+"22":"transparent",color:sel?p.color:T.muted,cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>
              {p.icon} {p.name}
            </button>
          ); })}
        </div>
        <button onClick={()=>setShowCal(true)} style={{width:"100%",padding:"10px 14px",border:`1px solid ${T.border}`,borderRadius:10,background:T.inputBg,color:T.text,cursor:"pointer",fontSize:14,fontFamily:"inherit",textAlign:"left",marginBottom:20}}>
          {"📅 "+dateLabel(form.date)+" ("+form.date+")"}
        </button>
        <button onClick={()=>onSave({...form,amount:parseFloat(form.amount)})} disabled={!form.amount} style={{width:"100%",padding:"14px",border:"none",borderRadius:12,background:form.amount?"linear-gradient(135deg,#6366F1,#8B5CF6)":"#334155",color:"#fff",cursor:form.amount?"pointer":"not-allowed",fontSize:15,fontWeight:700,fontFamily:"inherit"}}>
          {isEdit?"儲存變更":"新增記錄"}
        </button>
      </div>
    </div>
  );
}

function RecordCard({ r, allCategories, allTags, allPaymentMethods, onEdit, onDelete, T, isDesktop }) {
  const cat = allCategories.find(c=>c.name===r.category);
  const color = cat?.color||"#94A3B8";
  const D = isDesktop;
  const pm = allPaymentMethods?.find(p=>p.name===r.payment);
  return (
    <div style={{display:"flex",alignItems:"center",gap:D?12:10,padding:D?"12px 16px":"10px 12px",background:T.card,borderRadius:12,marginBottom:6}}>
      <div style={{width:D?44:36,height:D?44:36,borderRadius:12,background:color+"22",display:"flex",alignItems:"center",justifyContent:"center",fontSize:D?22:19,flexShrink:0}}>{cat?.icon||"支出"}</div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
          <span style={{fontSize:D?18:14,fontWeight:600,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.category}</span>
          {r.note&&<span style={{fontSize:D?15:12,color:T.textDim,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>· {r.note}</span>}
        </div>
        {r.tags?.length>0&&(
          <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
            {r.tags.map(t=>(
              <span key={t} style={{fontSize:11,padding:"2px 7px",borderRadius:12,background:tagColor(t,allTags)+"22",color:tagColor(t,allTags)}}>#{t}</span>
            ))}
          </div>
        )}
      </div>
      {D ? (
        <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:18,fontWeight:700,color:r.type==="income"?"#34D399":"#F87171"}}>{r.type==="income"?"+":"-"}${(r.amount||0).toLocaleString()}</div>
            {r.payment&&<div style={{fontSize:11,color:pm?.color||"#94A3B8",marginTop:2}}>{pm?.icon||""} {r.payment}</div>}
          </div>
          <button onClick={()=>onEdit(r)} style={{width:36,height:36,borderRadius:8,background:T.btnBg,border:"none",cursor:"pointer",fontSize:17,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>✏️</button>
          <button onClick={()=>onDelete(r)} style={{width:36,height:36,borderRadius:8,background:"#F8717122",border:"none",cursor:"pointer",fontSize:17,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>🗑️</button>
        </div>
      ) : (
        <div style={{textAlign:"right",flexShrink:0}}>
          <div style={{fontSize:15,fontWeight:700,color:r.type==="income"?"#34D399":"#F87171"}}>{r.type==="income"?"+":"-"}${(r.amount||0).toLocaleString()}</div>
          {r.payment&&<div style={{fontSize:11,color:pm?.color||"#94A3B8",marginTop:1}}>{pm?.icon||""} {r.payment}</div>}
          <div style={{display:"flex",gap:4,marginTop:4,justifyContent:"flex-end"}}>
            <button onClick={()=>onEdit(r)} style={{background:T.btnBg,border:"none",borderRadius:6,padding:"4px 10px",color:T.btnText,cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>編輯</button>
            <button onClick={()=>onDelete(r)} style={{background:"#F8717122",border:"none",borderRadius:6,padding:"4px 10px",color:"#F87171",cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>刪除</button>
          </div>
        </div>
      )}
    </div>
  );
}

const ONBOARDING_SLIDES = [
  { icon:"👋", title:"歡迎使用記帳本", content:"這是你的私人記帳空間！\n所有資料都雲端同步，只要登入後就能使用，完全私有。\n接下來幫你介紹每個功能", tab:null, targetTour:null },
  { icon:"💸", title:"支出 / 收入記帳", content:"點選「支出」或「收入」後輸入金額。\n付款方式可選現金、信用卡、轉帳、電子支付等，方便日後統計哪種付款最常用。\n備註欄可以補充說明。\n日期預設今天，點一下可以用日曆快速選日期，隨時記帳也更方便", tab:"add", targetTour:"type-toggle" },
  { icon:"🏷️", title:"類別", content:"點選一個符合費用的類別。\n右下角「管理」進入類別管理，可以新增自訂類別、修改圖示顏色、刪除不要的類別。\n支出和收入類別完全分開管理", tab:"add", targetTour:"category-grid" },
  { icon:"🗂️", title:"類別管理", content:"點「管理」進入類別管理。\n可以新增類別（選圖示、選顏色、填名稱）。\n也可以編輯現有類別的圖示顏色，或刪除不需要的類別。\n支出和收入類別分開管理", tab:"add", targetTour:"cat-manage-btn" },
  { icon:"🔖", title:"標籤", content:"標籤是類別下的細分。\n例如「餐飲」下 #咖啡、#火鍋、#便當等。\n輸入關鍵字後 Enter 新增；選好後，常用標籤會自動出現供選用。\n一筆記錄可以有多個標籤", tab:"add", targetTour:"tag-section" },
  { icon:"📊", title:"明細統計", content:"三格顯示本月收入、支出、餘額。\n圖表有兩種：類別分布（圓餅圖）、累積折線圖（消費趨勢）和付款方式統計。\n下方是明細列表：可以日 / 週 / 月組，點任一筆記錄可以編輯或刪除", tab:"list", targetTour:"list-charts" },
  { icon:"📈", title:"標籤統計", content:"切換「本月」或「本年」以不同區間看標籤花費。\n「全部類別」下拉可切換類別。\n點進標籤可以看相關記錄：今年模式下還有月份趨勢長條圖", tab:"tags", targetTour:"tag-scope-area" },
  { icon:"⚙️", title:"標籤管理", content:"點「管理」進入管理模式。\n每個標籤會出現「編輯」和「刪除」按鈕。\n0 筆記錄的孤立標籤也會顯示，可在這裡一併清理。\n按「完成」退出管理模式", tab:"tags", targetTour:"tag-manage-btn" },
  { icon:"🔄", title:"定期支出收入", content:"設定後每到了指定日期，就會自動新增一筆記錄，完全不用手動記。\n支援定期支出（房租、水電費）和定期收入（薪水等）。\n可以加標籤，方便在標籤統計裡長期追蹤", tab:"recurring", targetTour:"recurring-section" },
  { icon:"✅", title:"開始記帳了", content:"介紹完畢！\n之後可以點「說明」按鈕隨時重看一次。\n有任何不清楚的地方都可以翻出來複習", tab:null, targetTour:null },
];

function OnboardingWizard({ onComplete, T }) {
  const [step, setStep] = useState(0);
  const slide = ONBOARDING_SLIDES[step];
  const isLast = step === ONBOARDING_SLIDES.length - 1;
  return (
    <div style={{position:"fixed",inset:0,zIndex:1000,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:T.modalBase,borderRadius:20,padding:"32px 28px",maxWidth:380,width:"100%",boxShadow:"0 24px 80px rgba(0,0,0,0.5)"}}>
        <div style={{display:"flex",justifyContent:"center",gap:5,marginBottom:28}}>
          {ONBOARDING_SLIDES.map((_,i)=>(
            <div key={i} style={{height:5,borderRadius:3,background:i===step?"#6366F1":i<step?"#818CF855":"#334155",width:i===step?22:6,transition:"all 0.3s"}}/>
          ))}
        </div>
        <div style={{fontSize:54,textAlign:"center",marginBottom:14,lineHeight:1}}>{slide.icon}</div>
        <div style={{fontSize:19,fontWeight:700,color:T.text,textAlign:"center",marginBottom:12}}>{slide.title}</div>
        <div style={{fontSize:14,color:T.textSub,lineHeight:1.75,textAlign:"center",marginBottom:28,whiteSpace:"pre-line"}}>{slide.content}</div>
        <button onClick={()=>isLast?onComplete():setStep(s=>s+1)} style={{width:"100%",padding:"13px",border:"none",borderRadius:12,background:"linear-gradient(135deg,#6366F1,#8B5CF6)",color:"#fff",cursor:"pointer",fontSize:15,fontWeight:700,fontFamily:"inherit",marginBottom:8}}>
          {isLast?"開始使用 →":"下一步 →"}
        </button>
        {step > 0 && (
          <button onClick={()=>setStep(s=>s-1)} style={{width:"100%",padding:"9px",border:"none",background:"transparent",color:T.muted,cursor:"pointer",fontSize:13,fontFamily:"inherit",marginBottom:4}}>← 上一步</button>
        )}
        {!isLast&&(
          <button onClick={onComplete} style={{width:"100%",padding:"9px",border:"none",background:"transparent",color:T.textDim,cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>跳過介紹</button>
        )}
      </div>
    </div>
  );
}

function SpotlightTour({ onComplete, setTab, onAction, T }) {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const [searching, setSearching] = useState(false);
  const slide = ONBOARDING_SLIDES[step];
  const isLast = step === ONBOARDING_SLIDES.length - 1;
  const PAD = 10;

  useEffect(() => {
    setRect(null);
    if (slide.tab) setTab(slide.tab);
    if (onAction) onAction(slide.action || null);
    if (!slide.targetTour) { setSearching(false); return; }
    setSearching(true);
    const delay = slide.action ? 150 : 50;
    let retries = 0;
    let tid;
    const tryFind = () => {
      const el = document.querySelector('[data-tour="' + slide.targetTour + '"]');
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({top:r.top,left:r.left,width:r.width,height:r.height});
        setSearching(false);
      } else if (retries < 5) {
        retries++;
        tid = setTimeout(tryFind, 80);
      } else {
        setSearching(false);
      }
    };
    tid = setTimeout(tryFind, delay);
    return () => clearTimeout(tid);
  }, [step]);

  useEffect(() => {
    if (!slide.targetTour) return;
    const recalc = () => {
      const el = document.querySelector('[data-tour="' + slide.targetTour + '"]');
      if (el) { const r = el.getBoundingClientRect(); setRect({top:r.top,left:r.left,width:r.width,height:r.height}); }
    };
    window.addEventListener('resize', recalc);
    return () => window.removeEventListener('resize', recalc);
  }, [step]);

  const goNext = () => isLast ? onComplete() : setStep(s=>s+1);
  const goPrev = () => setStep(s=>s-1);

  const Dots = (
    <div style={{display:"flex",gap:4,marginBottom:12,justifyContent:"center"}}>
      {ONBOARDING_SLIDES.map((_,i)=>(
        <div key={i} style={{height:4,borderRadius:2,transition:"all 0.3s",background:i===step?"#6366F1":i<step?"#818CF866":"#334155",width:i===step?18:4}}/>
      ))}
    </div>
  );
  const Btns = (
    <div style={{display:"flex",gap:8,alignItems:"center",marginTop:14}}>
      {step>0&&<button onClick={goPrev} style={{padding:"8px 12px",border:`1px solid ${T.border}`,borderRadius:8,background:"transparent",color:T.muted,cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>← 上一步</button>}
      <button onClick={goNext} style={{flex:1,padding:"10px",border:"none",borderRadius:10,background:"linear-gradient(135deg,#6366F1,#8B5CF6)",color:"#fff",cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"inherit"}}>{isLast?"開始使用 →":"下一步 →"}</button>
      {!isLast&&<button onClick={onComplete} style={{padding:"8px",border:"none",background:"transparent",color:T.textDim,cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>跳過</button>}
    </div>
  );

  if (!rect && searching) {
    return <div style={{position:"fixed",inset:0,zIndex:99990,background:"rgba(0,0,0,0.7)",pointerEvents:"all"}} onClick={e=>e.stopPropagation()}/>;
  }
  if (!rect) {
    return (
      <>
        <div style={{position:"fixed",inset:0,zIndex:99990,background:"rgba(0,0,0,0.75)",pointerEvents:"all"}} onClick={e=>e.stopPropagation()}/>
        <div style={{position:"fixed",inset:0,zIndex:99999,display:"flex",alignItems:"center",justifyContent:"center",padding:20,pointerEvents:"none"}}>
          <div key={step} style={{background:T.modalBase,borderRadius:20,padding:"32px 28px",maxWidth:380,width:"100%",boxShadow:"0 24px 80px rgba(0,0,0,0.5)",pointerEvents:"all",animation:"tourFadeIn 0.3s ease"}}>
            {Dots}
            <div style={{fontSize:52,textAlign:"center",marginBottom:14,lineHeight:1}}>{slide.icon}</div>
            <div style={{fontSize:19,fontWeight:700,color:T.text,textAlign:"center",marginBottom:10}}>{slide.title}</div>
            <div style={{fontSize:14,color:T.textSub,lineHeight:1.75,textAlign:"center",whiteSpace:"pre-line"}}>{slide.content}</div>
            {Btns}
          </div>
        </div>
      </>
    );
  }

  const vw = window.innerWidth; const vh = window.innerHeight;
  const tooltipW = 300;
  const cx = rect.left + rect.width / 2;
  const isTopHalf = rect.top + rect.height / 2 < vh * 0.55;
  const tooltipLeft = Math.max(16, Math.min(cx - tooltipW/2, vw - tooltipW - 16));
  const cardBelow = rect.top + rect.height + PAD + 14;
  const cardAboveBottom = vh - (rect.top - PAD - 14);
  const tooltipPos = (isTopHalf && cardBelow <= vh - 80)
    ? { top: cardBelow }
    : (cardAboveBottom <= vh - 80)
    ? { bottom: cardAboveBottom }
    : { bottom: 20 };

  return (
    <>
      <style>{`@keyframes tourFadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}`}</style>
      <div style={{position:"fixed",inset:0,zIndex:99990,pointerEvents:"all"}} onClick={e=>e.stopPropagation()}/>
      <div style={{position:"fixed",top:rect.top-PAD,left:rect.left-PAD,width:rect.width+PAD*2,height:rect.height+PAD*2,borderRadius:14,boxShadow:"0 0 0 9999px rgba(0,0,0,0.7)",border:"2px solid #818CF8",zIndex:99995,pointerEvents:"none",transition:"all 0.35s ease"}}/>
      <div key={`tip-${step}`} style={{position:"fixed",...tooltipPos,left:tooltipLeft,width:tooltipW,maxHeight:"min(480px,72vh)",overflowY:"auto",background:T.modalBase,borderRadius:16,padding:"18px 18px 14px",boxShadow:"0 8px 32px rgba(0,0,0,0.45)",border:`1px solid ${T.border}`,zIndex:99999,pointerEvents:"all",animation:"tourFadeIn 0.3s ease"}}>
        {Dots}
        <div style={{fontSize:17,fontWeight:700,color:T.text,marginBottom:8}}>{slide.icon} {slide.title}</div>
        <div style={{fontSize:14,color:T.textSub,lineHeight:1.7,whiteSpace:"pre-line"}}>{slide.content}</div>
        {Btns}
      </div>
    </>
  );
}
function BudgetApp({ user, onLogout }) {
  const [darkMode, setDarkMode] = useState(()=>localStorage.getItem("dm")!=="false");
  const T = darkMode ? DARK : LIGHT;

  const [records, setRecords] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [recurringReady, setRecurringReady] = useState(false);
  const [categoryDocs, setCategoryDocs] = useState([]);
  const [tagDocs, setTagDocs] = useState([]);
  const [dbLoading, setDbLoading] = useState(true);
  const recurringApplied = useRef(false);

  const [tab, setTab] = useState("add");
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth());
  const [selectedDay, setSelectedDay] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [chartType, setChartType] = useState("pie");
  const [listView, setListView] = useState("day");

  const [editRecord, setEditRecord] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showManageCat, setShowManageCat] = useState(false);
  const [showCal, setShowCal] = useState(false);

  const [activeTag, setActiveTag] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [tagManageMode, setTagManageMode] = useState(false);
  const [deleteTagTarget, setDeleteTagTarget] = useState(null);
  const [editTagName, setEditTagName] = useState(null);
  const [editTagInput, setEditTagInput] = useState("");
  const [tagCatFilter, setTagCatFilter] = useState("全部");
  const [tagTypeFilter, setTagTypeFilter] = useState("expense");

  const [addRecurring, setAddRecurring] = useState(false);
  const [editRecurring, setEditRecurring] = useState(null);
  const [deleteRecurring, setDeleteRecurring] = useState(null);
  const [tagTimeScope, setTagTimeScope] = useState("month");
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [paymentMethodDocs, setPaymentMethodDocs] = useState([]);
  const [showManagePayment, setShowManagePayment] = useState(false);
  const [paymentModalIncome, setPaymentModalIncome] = useState(false);

  const [form, setForm] = useState({...EMPTY_FORM});
  const [tagInput, setTagInput] = useState("");
  const [saveError, setSaveError] = useState("");
  const tagRef = useRef(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  useEffect(()=>{ const h=()=>setIsDesktop(window.innerWidth>=768); window.addEventListener("resize",h); return()=>window.removeEventListener("resize",h); },[]);
  useEffect(()=>{ localStorage.setItem("dm",darkMode); },[darkMode]);

  useEffect(()=>{
    const u1=onSnapshot(collection(db,"users",user.uid,"records"),s=>{ setRecords(s.docs.map(d=>({id:d.id,...d.data()}))); setDbLoading(false); });
    const u2=onSnapshot(collection(db,"users",user.uid,"recurring"),s=>{ const r=s.docs.map(d=>({id:d.id,...d.data()})); r.sort((a,b)=>a.day-b.day); setRecurring(r); setRecurringReady(true); });
    const u3=onSnapshot(collection(db,"users",user.uid,"categories"),s=>{ setCategoryDocs(s.docs.map(d=>({id:d.id,...d.data()}))); });
    const u4=onSnapshot(collection(db,"users",user.uid,"tags"),s=>{ setTagDocs(s.docs.map(d=>({id:d.id,...d.data()}))); });
    const u5=onSnapshot(collection(db,"users",user.uid,"paymentMethods"),s=>{ const r=s.docs.map(d=>({id:d.id,...d.data()})); r.sort((a,b)=>(a.order||0)-(b.order||0)); setPaymentMethodDocs(r); });
    return ()=>{ u1(); u2(); u3(); u4(); u5(); };
  },[user.uid]);

  useEffect(()=>{
    if (!dbLoading && paymentMethodDocs.length === 0) {
      PAYMENT_METHODS.forEach((p,i) => addDoc(collection(db,"users",user.uid,"paymentMethods"),{...p,order:i}).catch(()=>{}));
    }
  },[dbLoading, paymentMethodDocs.length, user.uid]);

  useEffect(()=>{
    if (!dbLoading) {
      const existing = new Set(categoryDocs.map(c=>c.name));
      [...BASE_CATEGORIES, ...BASE_INCOME_CATEGORIES].forEach((c,i)=>{ if(!existing.has(c.name)) addDoc(collection(db,"users",user.uid,"categories"),{...c,order:i}).catch(()=>{}); });
    }
  },[dbLoading, user.uid]);

  useEffect(()=>{
    getDoc(doc(db,"users",user.uid,"settings","onboarding")).then(snap=>{ if(!snap.exists()) setShowOnboarding(true); }).catch(()=>{});
  },[user.uid]);

  const completeOnboarding = async () => {
    setShowOnboarding(false);
    setShowManageCat(false);
    setTagManageMode(false);
    try { await setDoc(doc(db,"users",user.uid,"settings","onboarding"),{done:true}); } catch(_) {}
  };

  const handleTourAction = (action) => {
    setShowManageCat(false);
    setTagManageMode(false);
    if (action === "openCatManage") setShowManageCat(true);
    if (action === "openTagManage") setTagManageMode(true);
  };

  useEffect(()=>{
    if (dbLoading || !recurringReady || recurringApplied.current) return;
    recurringApplied.current = true;
    const now = new Date();
    const todayDay = now.getDate();
    const thisYear = now.getFullYear();
    const thisMonth = now.getMonth();
    recurring.forEach(async item=>{
      const maxDay = new Date(thisYear, thisMonth+1, 0).getDate();
      const effectiveDay = Math.min(item.day, maxDay);
      if (todayDay < effectiveDay) return;
      const already = records.some(r=>r.recurringId===item.id && new Date(r.date+"T12:00:00").getFullYear()===thisYear && new Date(r.date+"T12:00:00").getMonth()===thisMonth);
      if (already) return;
      const dateStr = fmt(new Date(thisYear, thisMonth, effectiveDay));
      try {
        if (item.tags?.length > 0) { try { await ensureTagDocs(item.tags, item.category); } catch(_){} }
        await addDoc(collection(db,"users",user.uid,"records"),{
          type:item.type||"expense", amount:Number(item.amount), category:item.category,
          note:item.name+(item.note?" · "+item.note:""), date:dateStr,
          tags:item.tags||[], payment:"現金", recurringId:item.id,
        });
      } catch(e) { setSaveError(e.message); }
    });
  },[dbLoading, recurringReady, user.uid]);

  const allCategories = useMemo(()=>[...categoryDocs].sort((a,b)=>(a.order??99)-(b.order??99)),[categoryDocs]);
  const expenseCategories = useMemo(()=>allCategories.filter(c=>c.type!=="income"),[allCategories]);
  const incomeCategories = useMemo(()=>allCategories.filter(c=>c.type==="income"),[allCategories]);
  const allPaymentMethods = paymentMethodDocs;
  const allTags = useMemo(()=>{ const s=new Set(); records.forEach(r=>r.tags?.forEach(t=>s.add(t))); return s; },[records]);

  const monthRecords = useMemo(()=>records.filter(r=>{ const d=new Date(r.date+"T12:00:00"); return d.getFullYear()===filterYear&&d.getMonth()===filterMonth; }),[records,filterMonth,filterYear]);
  const tagScopeRecords = useMemo(()=>tagTimeScope==="month"
    ? records.filter(r=>{ const d=new Date(r.date+"T12:00:00"); return d.getFullYear()===filterYear&&d.getMonth()===filterMonth; })
    : records.filter(r=>new Date(r.date+"T12:00:00").getFullYear()===filterYear)
  ,[records,tagTimeScope,filterYear,filterMonth]);
  const expenses = useMemo(()=>monthRecords.filter(r=>r.type==="expense"),[monthRecords]);
  const totalExpense = expenses.reduce((s,r)=>s+r.amount,0);
  const totalIncome = monthRecords.filter(r=>r.type==="income").reduce((s,r)=>s+r.amount,0);

  const ensureTagDocs = async (tags, category) => {
    const newTags = tags.filter(name=>!tagDocs.some(t=>t.name===name&&t.categoryId===category));
    await Promise.all(newTags.map(name=>addDoc(collection(db,"users",user.uid,"tags"),{name,categoryId:category})));
  };

  const saveRecord = async (data) => {
    const { id, ...fields } = data;
    try {
      await ensureTagDocs(fields.tags||[], fields.category);
      if (id) { await updateDoc(doc(db,"users",user.uid,"records",id),fields); }
      else { await addDoc(collection(db,"users",user.uid,"records"),fields); }
      setEditRecord(null);
    } catch(e) { setSaveError(e.message); }
  };

  const deleteRecord = async (r) => { try { await deleteDoc(doc(db,"users",user.uid,"records",r.id)); setDeleteTarget(null); } catch(e) { setSaveError(e.message); } };

  const deleteTag = async (tagName) => {
    const batch = writeBatch(db);
    records.forEach(r=>{ if(r.tags?.includes(tagName)) batch.update(doc(db,"users",user.uid,"records",r.id),{tags:r.tags.filter(t=>t!==tagName)}); });
    tagDocs.filter(t=>t.name===tagName).forEach(td=>batch.delete(doc(db,"users",user.uid,"tags",td.id)));
    try { await batch.commit(); setDeleteTagTarget(null); if (activeTag===tagName) setActiveTag(null); }
    catch(e) { setSaveError(e.message); }
  };

  const renameTag = async (oldName, newName) => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldName) return;
    const batch = writeBatch(db);
    records.forEach(r=>{ if(r.tags?.includes(oldName)) batch.update(doc(db,"users",user.uid,"records",r.id),{tags:r.tags.map(t=>t===oldName?trimmed:t)}); });
    const td = tagDocs.find(t=>t.name===oldName);
    if (td) batch.update(doc(db,"users",user.uid,"tags",td.id),{name:trimmed});
    if(activeTag===oldName) setActiveTag(trimmed);
    try { await batch.commit(); }
    catch(e) { setSaveError(e.message); if(activeTag===trimmed) setActiveTag(oldName); }
  };

  const savePaymentMethod = async (data) => {
    try { await addDoc(collection(db,"users",user.uid,"paymentMethods"),{...data,order:allPaymentMethods.length}); }
    catch(e) { setSaveError(e.message); }
  };
  const deletePaymentMethod = async (id) => {
    try { await deleteDoc(doc(db,"users",user.uid,"paymentMethods",id)); }
    catch(e) { setSaveError(e.message); }
  };
  const updatePaymentMethod = async (id, data) => {
    try { await updateDoc(doc(db,"users",user.uid,"paymentMethods",id), data); }
    catch(e) { setSaveError(e.message); }
  };

  const saveCategory = async (data) => {
    try { await addDoc(collection(db,"users",user.uid,"categories"),data); }
    catch(e) { setSaveError(e.message); }
  };
  const updateCategory = async (id, data) => {
    try { await updateDoc(doc(db,"users",user.uid,"categories",id), data); }
    catch(e) { setSaveError(e.message); }
  };
  const deleteCategory = async (id) => {
    try { await deleteDoc(doc(db,"users",user.uid,"categories",id)); }
    catch(e) { setSaveError(e.message); }
  };
  const reorderCategories = async (items) => {
    const batch = writeBatch(db);
    items.forEach((item,i) => batch.update(doc(db,"users",user.uid,"categories",item.id),{order:i}));
    try { await batch.commit(); } catch(e) { setSaveError(e.message); }
  };
  const reorderPayments = async (items) => {
    const batch = writeBatch(db);
    items.forEach((item,i) => batch.update(doc(db,"users",user.uid,"paymentMethods",item.id),{order:i}));
    try { await batch.commit(); } catch(e) { setSaveError(e.message); }
  };

  const saveRecurring = async (data) => {
    const { id, ...fields } = data;
    try {
      if (fields.tags?.length > 0) { try { await ensureTagDocs(fields.tags, fields.category); } catch(_){} }
      if (id) { await updateDoc(doc(db,"users",user.uid,"recurring",id),fields); }
      else { await addDoc(collection(db,"users",user.uid,"recurring"),fields); }
      setAddRecurring(false); setEditRecurring(null);
    } catch(e) { setSaveError(e.message); }
  };
  const delRecurring = async (r) => { try { await deleteDoc(doc(db,"users",user.uid,"recurring",r.id)); setDeleteRecurring(null); } catch(e) { setSaveError(e.message); } };

  const catTags = useMemo(()=>tagDocs.filter(t=>t.categoryId===form.category).map(t=>t.name),[tagDocs,form.category]);
  const tagSuggestions = catTags.filter(t=>t.toLowerCase().includes(tagInput.toLowerCase())&&!form.tags.includes(t));

  const addFormTag = (name) => {
    const t = name.trim().replace(/^#+/,'');
    if (t&&!form.tags.includes(t)) setForm(f=>({...f,tags:[...f.tags,t]}));
    setTagInput("");
  };

  const submitRecord = async () => {
    const amt = parseFloat(form.amount);
    if (!amt||amt<=0) return;
    try {
      await ensureTagDocs(form.tags, form.category);
      await addDoc(collection(db,"users",user.uid,"records"),{...form,amount:amt});
      setForm({...EMPTY_FORM,date:today()});
      setTagInput("");
    } catch(e) { setSaveError(e.message); }
  };

  const hierarchy = useMemo(()=> allCategories.map(cat=>{
    const catRecs = records.filter(r=>r.category===cat.name);
    const tags = tagDocs.filter(t=>t.categoryId===cat.name).map(td=>({
      name:td.name, id:td.id,
      count:records.filter(r=>r.tags?.includes(td.name)).length,
      total:records.filter(r=>r.tags?.includes(td.name)&&r.type==="expense").reduce((s,r)=>s+r.amount,0),
      income:records.filter(r=>r.tags?.includes(td.name)&&r.type==="income").reduce((s,r)=>s+r.amount,0),
    }));
    return { ...cat, count:catRecs.length, expense:catRecs.filter(r=>r.type==="expense").reduce((s,r)=>s+r.amount,0), tags };
  }).filter(c=>c.tags.length>0), [allCategories, records, tagDocs]);

  const tagHierarchy = useMemo(()=> allCategories.map(cat=>{
    const catRecs = tagScopeRecords.filter(r=>r.category===cat.name);
    const tags = tagDocs.filter(t=>t.categoryId===cat.name).map(td=>({
      name:td.name, id:td.id,
      count:tagScopeRecords.filter(r=>r.tags?.includes(td.name)).length,
      total:tagScopeRecords.filter(r=>r.tags?.includes(td.name)&&r.type==="expense").reduce((s,r)=>s+r.amount,0),
      income:tagScopeRecords.filter(r=>r.tags?.includes(td.name)&&r.type==="income").reduce((s,r)=>s+r.amount,0),
    }));
    return { ...cat, count:catRecs.length, expense:catRecs.filter(r=>r.type==="expense").reduce((s,r)=>s+r.amount,0), tags };
  }).filter(c=>c.tags.length>0), [allCategories, tagScopeRecords, tagDocs]);

  const catData = useMemo(()=>{
    const m={};
    expenses.forEach(r=>{ m[r.category]=(m[r.category]||0)+r.amount; });
    return Object.entries(m).map(([name,value])=>({name,value,color:allCategories.find(c=>c.name===name)?.color||"#94A3B8"})).sort((a,b)=>b.value-a.value);
  },[expenses,allCategories]);

  const paymentData = useMemo(()=>{
    const m={};
    expenses.forEach(r=>{ const p=r.payment||"現金"; m[p]=(m[p]||0)+r.amount; });
    return Object.entries(m).map(([name,value])=>{ const pm=allPaymentMethods.find(p=>p.name===name); return {name,value,color:pm?.color||"#94A3B8",icon:pm?.icon||"💵"}; }).sort((a,b)=>b.value-a.value);
  },[expenses,allPaymentMethods]);

  useEffect(()=>{ setSelectedDay(null); },[listView,filterMonth,filterYear]);
  useEffect(()=>{ const fn=()=>setShowScrollTop(window.scrollY>300); window.addEventListener("scroll",fn); return ()=>window.removeEventListener("scroll",fn); },[]);

  const dailyData = useMemo(()=>{
    const m={}; expenses.forEach(r=>{ m[r.date]=(m[r.date]||0)+r.amount; });
    const days=new Date(filterYear,filterMonth+1,0).getDate();
    return Array.from({length:days},(_,i)=>{ const d=`${filterYear}-${String(filterMonth+1).padStart(2,"0")}-${String(i+1).padStart(2,"0")}`; return {label:i+1,value:m[d]||0}; });
  },[expenses,filterYear,filterMonth]);

  const weeklyData = useMemo(()=>{
    const m={}; expenses.forEach(r=>{ const wk=getWeekKey(r.date); m[wk]=(m[wk]||0)+r.amount; });
    return Object.entries(m).sort((a,b)=>a[0].localeCompare(b[0])).map(([key,value])=>({label:weekLabel(key),value}));
  },[expenses]);

  const monthlyData = useMemo(()=>MONTHS.map((label,i)=>({
    label,
    value: records.filter(r=>{ const d=new Date(r.date+"T12:00:00"); return d.getFullYear()===filterYear&&d.getMonth()===i&&r.type==="expense"; }).reduce((s,r)=>s+r.amount,0)
  })),[records,filterYear]);

  const groupedRecords = useMemo(()=>{
    const sorted=[...monthRecords].sort((a,b)=>b.date.localeCompare(a.date));
    const groups={};
    sorted.forEach(r=>{ const k=listView==="day"?r.date:listView==="week"?getWeekKey(r.date):r.date.slice(0,7); if(!groups[k])groups[k]=[]; groups[k].push(r); });
    return Object.entries(groups).sort((a,b)=>b[0].localeCompare(a[0]));
  },[monthRecords,listView]);

  const recurringWithDays = useMemo(()=>recurring.map(r=>{
    const now=new Date(); const d=now.getDate();
    let next=new Date(now.getFullYear(),now.getMonth(),r.day);
    if(next.getDate()<d)next=new Date(now.getFullYear(),now.getMonth()+1,r.day);
    return {...r,daysLeft:Math.round((next-now)/86400000)};
  }),[recurring]);

  const tagDetailRecs = useMemo(()=>activeTag?tagScopeRecords.filter(r=>r.tags?.includes(activeTag)&&(tagCatFilter==="全部"||r.category===tagCatFilter)):[]
  ,[tagScopeRecords,activeTag,tagCatFilter]);

  const tagCats = useMemo(()=>activeTag?[...new Set(tagScopeRecords.filter(r=>r.tags?.includes(activeTag)).map(r=>r.category))]:[]
  ,[tagScopeRecords,activeTag]);

  const tagMonthlyData = useMemo(()=>{
    if(!activeTag||tagTimeScope!=="year") return [];
    return MONTHS.map((label,month)=>{
      const recs=records.filter(r=>{ const d=new Date(r.date+"T12:00:00"); return d.getFullYear()===filterYear&&d.getMonth()===month&&r.tags?.includes(activeTag); });
      return { label, expense:recs.filter(r=>r.type==="expense").reduce((s,r)=>s+r.amount,0), income:recs.filter(r=>r.type==="income").reduce((s,r)=>s+r.amount,0) };
    });
  },[activeTag,tagTimeScope,records,filterYear]);

  if (dbLoading) return <div style={{background:T.bg,minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center"}}><div style={{color:T.muted,fontSize:14}}>載入中.....</div></div>;

  const navItems = [["add","📝","記帳"],["list","📊","明細"],["tags","🏷️","標籤"],["recurring","🔄","定期"]];
  const catForForm = (form.type==="income"?incomeCategories:expenseCategories).find(c=>c.name===form.category) || allCategories.find(c=>c.name===form.category);
  const toggleExpand = name => setExpandedCategories(p=>({...p,[name]:!p[name]}));

  const prevMonth = () => { if(filterMonth===0){setFilterYear(y=>y-1);setFilterMonth(11);}else setFilterMonth(m=>m-1); };
  const nextMonth = () => { if(filterMonth===11){setFilterYear(y=>y+1);setFilterMonth(0);}else setFilterMonth(m=>m+1); };

  // ===== Sidebar =====
  const Sidebar = (
    <div style={{width:240,background:T.sidebarBg,borderRight:`1px solid ${T.border}`,display:"flex",flexDirection:"column",height:"100vh",position:"fixed",left:0,top:0,zIndex:10}}>
      <div style={{padding:"24px 20px 16px"}}>
        <div style={{fontSize:11,letterSpacing:3,color:T.muted,marginBottom:4}}>MY BUDGET</div>
        <div style={{fontSize:22,fontWeight:700,color:T.text}}>記帳本</div>
        <div style={{display:"flex",alignItems:"center",gap:8,marginTop:8}}>
          <button onClick={prevMonth} style={{background:T.btnBg,border:"none",borderRadius:6,width:24,height:24,cursor:"pointer",color:T.btnText,fontSize:14}}>{"<"}</button>
          <span style={{fontSize:13,color:T.textSub}}>{filterYear}年{MONTHS[filterMonth]}</span>
          <button onClick={nextMonth} style={{background:T.btnBg,border:"none",borderRadius:6,width:24,height:24,cursor:"pointer",color:T.btnText,fontSize:14}}>{">"}</button>
        </div>
      </div>
      <div>
        {navItems.map(([v,icon,label])=>(
          <button key={v} onClick={()=>{setTab(v);setActiveTag(null);}} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"12px 20px",border:"none",borderRadius:0,background:tab===v?T.card:"transparent",color:tab===v?T.text:T.muted,cursor:"pointer",fontSize:15,fontFamily:"inherit",marginBottom:0,textAlign:"left",fontWeight:tab===v?600:400,borderLeft:tab===v?`3px solid #818CF8`:"3px solid transparent"}}>
            <span style={{fontSize:18,width:22,textAlign:"center",flexShrink:0}}>{icon}</span>{label}
          </button>
        ))}
      </div>
      <div style={{flex:1}}/>
      <div style={{padding:"16px 20px",borderTop:`1px solid ${T.border}`}}>
        {[["本月收入","#34D399","+$"+totalIncome.toLocaleString()],["本月支出","#F87171","-$"+totalExpense.toLocaleString()],["餘額",totalIncome-totalExpense>=0?"#34D399":"#F87171","$"+(totalIncome-totalExpense).toLocaleString()]].map(([l,c,v],i)=>(
          <div key={l} style={{display:"flex",justifyContent:"space-between",marginBottom:i<2?6:0,paddingTop:i===2?8:0,borderTop:i===2?`1px solid ${T.border}`:"none"}}>
            <span style={{fontSize:12,color:T.muted}}>{l}</span>
            <span style={{fontSize:i===2?14:13,fontWeight:i===2?700:600,color:c}}>{v}</span>
          </div>
        ))}
      </div>
      <div style={{padding:"12px 20px",borderTop:`1px solid ${T.border}`,display:"flex",gap:8,flexWrap:"wrap"}}>
        <button onClick={()=>setDarkMode(d=>!d)} style={{flex:1,padding:"8px",border:`1px solid ${T.border}`,borderRadius:8,background:"transparent",color:T.muted,cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>{darkMode?"☀️":"🌙"}</button>
        <button onClick={onLogout} style={{flex:1,padding:"8px",border:`1px solid ${T.border}`,borderRadius:8,background:"transparent",color:T.muted,cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>登出</button>
        <button onClick={()=>setShowOnboarding(true)} style={{width:"100%",padding:"8px",border:`1px solid ${T.border}`,borderRadius:8,background:"transparent",color:T.muted,cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>說明</button>
      </div>
    </div>
  );

  // ?�?�?Mobile header ?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?
  const MobileHeader = (
    <div style={{background:T.headerBg,padding:"12px 16px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:56,zIndex:10}}>
      <div>
        <div style={{fontSize:16,fontWeight:700,color:T.text}}>記帳本</div>
        <div style={{fontSize:11,color:T.muted}}>{filterYear}年{MONTHS[filterMonth]}</div>
      </div>
      <div style={{display:"flex",gap:6,alignItems:"center"}}>
        <button onClick={prevMonth} style={{background:T.btnBg,border:"none",borderRadius:8,width:30,height:30,cursor:"pointer",color:T.btnText,fontSize:16}}>{"<"}</button>
        <button onClick={nextMonth} style={{background:T.btnBg,border:"none",borderRadius:8,width:30,height:30,cursor:"pointer",color:T.btnText,fontSize:16}}>{">"}</button>
        <button onClick={()=>setDarkMode(d=>!d)} style={{background:T.btnBg,border:"none",borderRadius:8,width:30,height:30,cursor:"pointer",color:T.btnText,fontSize:14}}>{darkMode?"☀️":"🌙"}</button>
        <button onClick={()=>setShowOnboarding(true)} style={{background:T.btnBg,border:"none",borderRadius:8,width:30,height:30,cursor:"pointer",color:T.btnText,fontSize:14}}>?</button>
        <button onClick={onLogout} style={{background:T.btnBg,border:"none",borderRadius:8,width:30,height:30,cursor:"pointer",color:T.btnText,fontSize:12,fontFamily:"inherit"}}>登出</button>
      </div>
    </div>
  );

  // ?�?�?Add Tab ?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?
  const renderAddTab = () => (
    <div style={{padding:isDesktop?"28px 32px":"16px",maxWidth:isDesktop?"none":500,width:"100%",boxSizing:"border-box"}}>
      <div data-tour="type-toggle" style={{display:"flex",borderRadius:14,overflow:"hidden",border:`1px solid ${T.border}`,marginBottom:16}}>
        {[["expense","支出"],["income","收入"]].map(([val,label])=>(
          <button key={val} onClick={()=>{ const cats=val==="income"?incomeCategories:expenseCategories; setForm(f=>({...f,type:val,category:cats[0]?.name||f.category,tags:[]})); }} style={{flex:1,padding:"12px",border:"none",background:form.type===val?(val==="expense"?"#F87171":"#34D399"):T.card,color:form.type===val?"#fff":T.muted,cursor:"pointer",fontSize:isDesktop?19:16,fontWeight:form.type===val?700:400,fontFamily:"inherit",transition:"all 0.2s"}}>{label}</button>
        ))}
      </div>
      <div style={{display:"flex",alignItems:"center",background:T.card,borderRadius:16,padding:"8px 16px",marginBottom:16,border:`1px solid ${T.border}`}}>
        <span style={{fontSize:20,color:T.muted,marginRight:8}}>$</span>
        <input type="number" min="0" placeholder="0" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))} style={{flex:1,border:"none",background:"transparent",color:T.text,fontSize:isDesktop?32:28,fontFamily:"'DM Mono',monospace",fontWeight:700,outline:"none"}}/>
      </div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
        <div style={{fontSize:isDesktop?15:12,color:T.muted,letterSpacing:2}}>類別</div>
        <button data-tour="cat-manage-btn" onClick={()=>setShowManageCat(true)} style={{background:"transparent",border:"none",color:T.textDim,cursor:"pointer",fontSize:12,fontFamily:"inherit",padding:"4px 8px"}}>管理</button>
      </div>
      <div data-tour="category-grid" style={{display:"grid",gridTemplateColumns:`repeat(auto-fill,minmax(${isDesktop?100:76}px,1fr))`,gap:8,marginBottom:16}}>
        {(form.type==="income"?incomeCategories:expenseCategories).map(c=>(
          <button key={c.name} onClick={()=>setForm(f=>({...f,category:c.name,tags:[]}))} style={{padding:"10px 4px",border:`1px solid ${form.category===c.name?c.color:T.border}`,borderRadius:12,background:form.category===c.name?c.color+"22":"transparent",color:form.category===c.name?c.color:T.muted,cursor:"pointer",fontFamily:"inherit",display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
            <span style={{fontSize:isDesktop?22:20}}>{c.icon}</span>
            <span style={{fontSize:isDesktop?15:12,whiteSpace:"nowrap"}}>{c.name}</span>
          </button>
        ))}
      </div>
      <div style={{fontSize:isDesktop?15:12,color:T.muted,letterSpacing:2,marginBottom:8}}>
        標籤 <span style={{color:T.textDim,fontSize:isDesktop?14:11,letterSpacing:0}}>（{catForForm?.icon} {form.category} 的標籤）</span>
      </div>
      {form.tags.length>0&&(
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:8}}>
          {form.tags.map(t=>(
            <span key={t} style={{display:"flex",alignItems:"center",gap:4,padding:"4px 10px",background:T.btnBg,borderRadius:20,fontSize:isDesktop?16:14,color:T.text}}>
              #{t}
              <button onClick={()=>setForm(f=>({...f,tags:f.tags.filter(x=>x!==t)}))} style={{background:"none",border:"none",cursor:"pointer",color:T.muted,padding:0,fontSize:14,lineHeight:1}}>x</button>
            </span>
          ))}
        </div>
      )}
      <div data-tour="tag-section" style={{position:"relative",marginBottom:8}}>
        <div style={{display:"flex",gap:6}}>
          <input ref={tagRef} placeholder="輸入標籤，Enter 新增" value={tagInput} onChange={e=>setTagInput(e.target.value)} onKeyDown={e=>{ if(e.key==="Enter"&&tagInput.trim()){e.preventDefault();addFormTag(tagInput);} }} style={{flex:1,padding:"10px 12px",border:`1px solid ${T.border}`,borderRadius:10,background:T.inputBg,color:T.text,fontSize:isDesktop?17:14,fontFamily:"inherit",outline:"none"}}/>
          <button onClick={()=>addFormTag(tagInput)} disabled={!tagInput.trim()} style={{padding:"10px 14px",border:"none",borderRadius:10,background:tagInput.trim()?"#6366F1":"#334155",color:"#fff",cursor:tagInput.trim()?"pointer":"not-allowed",fontSize:isDesktop?17:14,fontFamily:"inherit"}}>+</button>
        </div>
        {tagSuggestions.length>0&&tagInput&&(
          <div style={{position:"absolute",top:"100%",left:0,right:0,background:T.card,border:`1px solid ${T.border}`,borderRadius:10,marginTop:4,zIndex:10,overflow:"hidden"}}>
            {tagSuggestions.slice(0,5).map(s=>(
              <button key={s} onClick={()=>addFormTag(s)} style={{display:"block",width:"100%",padding:"8px 12px",border:"none",background:"transparent",color:T.text,cursor:"pointer",fontSize:isDesktop?17:14,fontFamily:"inherit",textAlign:"left"}}>#{s}</button>
            ))}
          </div>
        )}
      </div>
      {(()=>{ const avail=catTags.filter(t=>!form.tags.includes(t)); return avail.length>0&&(
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:12}}>
          {avail.map(t=>(
            <button key={t} onClick={()=>addFormTag(t)} style={{padding:"4px 10px",border:`1px solid ${T.border}`,borderRadius:20,background:"transparent",color:T.textSub,cursor:"pointer",fontSize:isDesktop?17:14,fontFamily:"inherit"}}>#{t}</button>
          ))}
        </div>
      ); })()}
      <div style={{fontSize:isDesktop?15:12,color:T.muted,letterSpacing:2,marginBottom:6}}>備註</div>
      <input placeholder="備註（選填）" value={form.note} onChange={e=>setForm(f=>({...f,note:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${T.border}`,borderRadius:10,background:T.inputBg,color:T.text,fontSize:isDesktop?19:16,fontFamily:"inherit",boxSizing:"border-box",marginBottom:12}}/>
      <button onClick={()=>setShowCal(true)} style={{width:"100%",padding:"10px 14px",border:`1px solid ${T.border}`,borderRadius:10,background:T.inputBg,color:T.text,cursor:"pointer",fontSize:isDesktop?19:16,fontFamily:"inherit",textAlign:"left",marginBottom:16}}>
        {"📅 "+dateLabel(form.date)+" ("+form.date+")"}
      </button>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
        <div style={{fontSize:isDesktop?15:12,color:T.muted,letterSpacing:2}}>{form.type==="income"?"收款方式":"付款方式"}</div>
        <button onClick={()=>{setShowManagePayment(true);setPaymentModalIncome(form.type==="income");}} style={{background:"transparent",border:"none",color:T.textDim,cursor:"pointer",fontSize:12,fontFamily:"inherit",padding:"4px 8px"}}>管理</button>
      </div>
      <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:16}}>
        {allPaymentMethods.map(p=>(
          <button key={p.name} onClick={()=>setForm(f=>({...f,payment:p.name}))} style={{padding:"8px 14px",border:`1px solid ${form.payment===p.name?p.color:T.border}`,borderRadius:10,background:form.payment===p.name?p.color+"22":"transparent",color:form.payment===p.name?p.color:T.muted,cursor:"pointer",fontSize:isDesktop?16:14,fontFamily:"inherit"}}>
            {p.icon} {p.name}
          </button>
        ))}
      </div>
      <button onClick={submitRecord} disabled={!form.amount} style={{width:"100%",padding:"16px",border:"none",borderRadius:14,background:form.amount?"linear-gradient(135deg,#6366F1,#8B5CF6)":"#334155",color:"#fff",cursor:form.amount?"pointer":"not-allowed",fontSize:isDesktop?18:16,fontWeight:700,fontFamily:"inherit"}}>
        {form.type==="expense"?"記帳支出":"記帳收入"}
      </button>
    </div>
  );

  // ?�?�?List Tab ?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?
  const renderListTab = () => (
    <div style={{padding:isDesktop?"24px":"16px"}}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:20}}>
        {[["收入","#34D399",totalIncome],["支出","#F87171",totalExpense],["餘額",totalIncome-totalExpense>=0?"#34D399":"#F87171",totalIncome-totalExpense]].map(([label,color,val])=>(
          <div key={label} style={{background:T.card,borderRadius:12,padding:"12px",textAlign:"center"}}>
            <div style={{fontSize:isDesktop?15:12,color:T.muted,marginBottom:4}}>{label}</div>
            <div style={{fontSize:isDesktop?20:16,fontWeight:700,color}}>${val.toLocaleString()}</div>
          </div>
        ))}
      </div>
      <div data-tour="list-charts">
      <div style={{background:T.card,borderRadius:14,padding:"16px",marginBottom:16}}>
        <div style={{display:"flex",gap:6,marginBottom:14}}>
          {[["pie","類別"],["line","累積"],["payment","付款"]].map(([key,label])=>(
            <button key={key} onClick={()=>setChartType(key)} style={{flex:1,padding:"7px",border:`1px solid ${chartType===key?"#6366F1":T.border}`,borderRadius:8,background:chartType===key?"#6366F122":"transparent",color:chartType===key?"#818CF8":T.muted,cursor:"pointer",fontSize:isDesktop?16:13,fontFamily:"inherit"}}>{label}</button>
          ))}
        </div>
        {chartType==="pie"&&catData.length>0&&(
          <div>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart><Pie data={catData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={catData.length>1?3:0} dataKey="value">{catData.map((e,i)=><Cell key={i} fill={e.color}/>)}</Pie><Tooltip formatter={v=>`$${v.toLocaleString()}`}/></PieChart>
            </ResponsiveContainer>
            <div style={{display:"flex",flexWrap:"wrap",gap:8,marginTop:8}}>
              {catData.map(d=>(<div key={d.name} style={{display:"flex",alignItems:"center",gap:6}}><div style={{width:10,height:10,borderRadius:"50%",background:d.color}}/><span style={{fontSize:12,color:T.textSub}}>{d.name} ${d.value.toLocaleString()}</span></div>))}
            </div>
          </div>
        )}
        {chartType==="line"&&<ResponsiveContainer width="100%" height={180}><LineChart data={dailyData.reduce((acc,cur)=>{ const p=acc.length?acc[acc.length-1].cum:0; acc.push({label:cur.label,cum:p+cur.value}); return acc; },[])}><XAxis dataKey="label" tick={{fill:T.muted,fontSize:10}}/><YAxis tick={{fill:T.muted,fontSize:10}}/><Tooltip formatter={v=>[`$${v.toLocaleString()}`,"累計"]}/><Line type="monotone" dataKey="cum" stroke="#6366F1" strokeWidth={2} dot={false}/></LineChart></ResponsiveContainer>}
        {chartType==="payment"&&paymentData.length>0&&(
          <div>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart><Pie data={paymentData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={paymentData.length>1?3:0} dataKey="value">{paymentData.map((e,i)=><Cell key={i} fill={e.color}/>)}</Pie><Tooltip formatter={v=>`$${v.toLocaleString()}`}/></PieChart>
            </ResponsiveContainer>
            <div style={{display:"flex",flexWrap:"wrap",gap:8,marginTop:8}}>
              {paymentData.map(d=>(<div key={d.name} style={{display:"flex",alignItems:"center",gap:6}}><div style={{width:10,height:10,borderRadius:"50%",background:d.color}}/><span style={{fontSize:12,color:T.textSub}}>{d.icon} {d.name} ${d.value.toLocaleString()}</span></div>))}
            </div>
          </div>
        )}
        {catData.length===0&&<div style={{textAlign:"center",padding:"40px 0",color:T.textDim,fontSize:14}}>本月沒有支出記錄</div>}
      </div>
      <div style={{display:"flex",gap:6,marginBottom:12}}>
        {[["day","日"],["week","週"],["month","月"]].map(([v,l])=>(
          <button key={v} onClick={()=>setListView(v)} style={{flex:1,padding:"7px",border:`1px solid ${listView===v?"#6366F1":T.border}`,borderRadius:8,background:listView===v?"#6366F122":"transparent",color:listView===v?"#818CF8":T.muted,cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>{l}</button>
        ))}
      </div>
      {(()=>{ const bd=listView==="day"?dailyData:listView==="week"?weeklyData:monthlyData; if(!bd.some(d=>d.value>0)) return null; return (<div style={{background:T.card,borderRadius:14,padding:"12px 16px",marginBottom:16}}><ResponsiveContainer width="100%" height={140}><BarChart data={bd}><XAxis dataKey="label" tick={{fill:T.muted,fontSize:10}}/><YAxis tick={{fill:T.muted,fontSize:10}} width={45}/><Tooltip formatter={v=>[`$${v.toLocaleString()}`,"支出"]}/><Bar dataKey="value" radius={[4,4,0,0]} cursor={listView==="day"?"pointer":"default"} onClick={(data)=>{ if(listView!=="day"||!data.value) return; const day=data.label; setSelectedDay(d=>d===day?null:day); setTimeout(()=>{ const m=String(filterMonth+1).padStart(2,"0"); const d2=String(day).padStart(2,"0"); const dateStr=filterYear+"-"+m+"-"+d2; const el=document.getElementById("day-"+dateStr); if(el) el.scrollIntoView({behavior:"smooth",block:"start"}); },50); }}>{bd.map((e,i)=><Cell key={i} fill={selectedDay===e.label?"#A78BFA":selectedDay?"#6366F155":"#6366F1"}/>)}</Bar></BarChart></ResponsiveContainer></div>); })()}
      {groupedRecords.length===0&&<div style={{textAlign:"center",padding:"40px 0",color:T.textDim}}>本月沒有記錄</div>}
      </div>
      {groupedRecords.map(([key,recs])=>{
        const grpExp=recs.filter(r=>r.type==="expense").reduce((s,r)=>s+r.amount,0);
        const grpInc=recs.filter(r=>r.type==="income").reduce((s,r)=>s+r.amount,0);
        return (
          <div key={key} id={"day-"+key} style={{marginBottom:16}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,padding:"0 4px"}}>
              <span style={{fontSize:13,fontWeight:600,color:T.textSub,fontSize:isDesktop?15:13}}>{listView==="day"?dateLabel(key):key}</span>
              <div style={{display:"flex",gap:8}}>
                {grpExp>0&&<span style={{fontSize:12,color:"#F87171"}}>-${grpExp.toLocaleString()}</span>}
                {grpInc>0&&<span style={{fontSize:12,color:"#34D399"}}>+${grpInc.toLocaleString()}</span>}
              </div>
            </div>
            {recs.map(r=><RecordCard key={r.id} r={r} allCategories={allCategories} allTags={allTags} allPaymentMethods={allPaymentMethods} onEdit={setEditRecord} onDelete={setDeleteTarget} T={T} isDesktop={isDesktop}/>)}
          </div>
        );
      })}
    </div>
  );

  // ?�?�?Tags Tab ?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?
  const TagScopeToggle = (
    <div style={{display:"flex",borderRadius:10,overflow:"hidden",border:`1px solid ${T.border}`,marginBottom:12}}>
      {[["month","本月"],["year","今年"]].map(([v,l])=>(
        <button key={v} onClick={()=>setTagTimeScope(v)} style={{flex:1,padding:"8px",border:"none",background:tagTimeScope===v?"#6366F133":T.card,color:tagTimeScope===v?"#818CF8":T.muted,cursor:"pointer",fontSize:13,fontWeight:tagTimeScope===v?700:400,fontFamily:"inherit"}}>{l}</button>
      ))}
    </div>
  );
  const tagScopeLabel = tagTimeScope==="month"?`${filterYear}年${MONTHS[filterMonth]}`:`${filterYear}年`;

  const TagDetailModal = activeTag ? createPortal((
    <div onClick={()=>{setActiveTag(null);setTagCatFilter("全部");setEditTagName(null);}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
      <div onClick={e=>e.stopPropagation()} style={{background:T.modalBase,borderRadius:"20px",width:"min(640px,92vw)",maxHeight:"80vh",display:"flex",flexDirection:"column",overflow:"hidden"}}>
        <div style={{padding:"20px 20px 16px",borderBottom:"1px solid "+T.border,flexShrink:0}}>
          {editTagName===activeTag ? (
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <input autoFocus value={editTagInput} onChange={e=>setEditTagInput(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"){renameTag(activeTag,editTagInput);setEditTagName(null);}if(e.key==="Escape")setEditTagName(null);}} style={{flex:1,fontSize:18,fontWeight:700,padding:"6px 10px",border:"1px solid "+T.border,borderRadius:8,background:T.inputBg,color:T.text,fontFamily:"inherit",minWidth:0}}/>
              <button onClick={()=>{renameTag(activeTag,editTagInput);setEditTagName(null);}} style={{background:"#6366F1",border:"none",borderRadius:8,padding:"6px 14px",color:"#fff",cursor:"pointer",fontSize:13,fontFamily:"inherit",flexShrink:0}}>確認</button>
              <button onClick={()=>setEditTagName(null)} style={{background:T.btnBg,border:"none",borderRadius:8,padding:"6px 10px",color:T.muted,cursor:"pointer",fontSize:13,fontFamily:"inherit",flexShrink:0}}>取消</button>
            </div>
          ) : (
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <span style={{fontSize:22,fontWeight:700,color:tagColor(activeTag,allTags)}}>#{activeTag}</span>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <button onClick={()=>{setEditTagName(activeTag);setEditTagInput(activeTag);}} style={{background:T.btnBg,border:"none",borderRadius:8,padding:"6px 12px",color:T.muted,cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>編輯名稱</button>
                <button onClick={()=>setDeleteTagTarget(activeTag)} style={{background:"#F8717122",border:"none",borderRadius:8,padding:"6px 12px",color:"#F87171",cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>刪除標籤</button>
                <button onClick={()=>{setActiveTag(null);setTagCatFilter("全部");setEditTagName(null);}} style={{background:T.btnBg,border:"none",borderRadius:8,width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:T.muted,fontSize:18,fontFamily:"inherit"}}>×</button>
              </div>
            </div>
          )}
        </div>
        <div style={{overflowY:"auto",padding:"20px",flex:1}}>
          {TagScopeToggle}
          <div style={{fontSize:12,color:T.muted,marginBottom:12}}>{tagScopeLabel}</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:16}}>
            {[["筆數","#818CF8",tagDetailRecs.length+" 筆"],["支出","#F87171","$"+tagDetailRecs.filter(r=>r.type==="expense").reduce((s,r)=>s+r.amount,0).toLocaleString()],["收入","#34D399","$"+tagDetailRecs.filter(r=>r.type==="income").reduce((s,r)=>s+r.amount,0).toLocaleString()]].map(([l,c,v])=>(
              <div key={l} style={{background:T.card,borderRadius:12,padding:"12px",textAlign:"center"}}>
                <div style={{fontSize:11,color:T.muted,marginBottom:4}}>{l}</div>
                <div style={{fontSize:14,fontWeight:700,color:c}}>{v}</div>
              </div>
            ))}
          </div>
          {tagTimeScope==="year"&&tagMonthlyData.some(d=>d.expense>0||d.income>0)&&(
            <div style={{background:T.card,borderRadius:14,padding:"12px 16px",marginBottom:16,border:"1px solid "+T.border}}>
              <div style={{fontSize:11,color:T.muted,letterSpacing:2,marginBottom:8}}>月份趨勢</div>
              <ResponsiveContainer width="100%" height={120}>
                <BarChart data={tagMonthlyData} barSize={12}>
                  <XAxis dataKey="label" tick={{fill:T.muted,fontSize:10}} axisLine={false} tickLine={false}/>
                  <Tooltip formatter={(v,name)=>["$"+v.toLocaleString(),name==="expense"?"支出":"收入"]}/>
                  {tagMonthlyData.some(d=>d.expense>0)&&<Bar dataKey="expense" fill="#F87171" radius={[3,3,0,0]}/>}
                  {tagMonthlyData.some(d=>d.income>0)&&<Bar dataKey="income" fill="#34D399" radius={[3,3,0,0]}/>}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {tagCats.length>1&&(
            <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:12}}>
              {["全部",...tagCats].map(c=>(
                <button key={c} onClick={()=>setTagCatFilter(c)} style={{padding:"6px 12px",border:"1px solid "+(tagCatFilter===c?"#6366F1":T.border),borderRadius:20,background:tagCatFilter===c?"#6366F122":"transparent",color:tagCatFilter===c?"#818CF8":T.muted,cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>{c}</button>
              ))}
            </div>
          )}
          {tagDetailRecs.sort((a,b)=>b.date.localeCompare(a.date)).map(r=><RecordCard key={r.id} r={r} allCategories={allCategories} allTags={allTags} allPaymentMethods={allPaymentMethods} onEdit={setEditRecord} onDelete={setDeleteTarget} T={T} isDesktop={isDesktop}/>)}
          {tagDetailRecs.length===0&&<div style={{textAlign:"center",padding:"40px 0",color:T.textDim,fontSize:14}}>沒有記錄</div>}
        </div>
      </div>
    </div>
  ), document.body) : null;
  const renderTagsTab = () => (
    <div style={{padding:isDesktop?"24px":"16px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div style={{display:"flex",gap:6}}>
          {(()=>{ const cats=tagHierarchy.filter(cat=>tagTypeFilter==="income"?cat.type==="income":cat.type!=="income"); const allExpanded=cats.length>0&&cats.every(c=>expandedCategories[c.name]); return cats.length>0&&(<button onClick={()=>{ const next={}; cats.forEach(c=>{next[c.name]=!allExpanded;}); setExpandedCategories(p=>({...p,...next})); }} style={{background:T.btnBg,border:"none",borderRadius:8,padding:"6px 12px",color:T.btnText,cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>{allExpanded?"全部收起":"全部展開"}</button>); })()}
        </div>
      </div>
      <div data-tour="tag-scope-area">
      {TagScopeToggle}
      <div style={{fontSize:12,color:T.muted,marginBottom:12}}>{tagScopeLabel}</div>
      <div style={{display:"flex",borderRadius:10,overflow:"hidden",border:`1px solid ${T.border}`,marginBottom:16}}>
        {[["expense","支出標籤"],["income","收入標籤"]].map(([v,l])=>(
          <button key={v} onClick={()=>setTagTypeFilter(v)} style={{flex:1,padding:"8px",border:"none",background:tagTypeFilter===v?(v==="expense"?"#F87171":"#34D399"):T.card,color:tagTypeFilter===v?"#fff":T.muted,cursor:"pointer",fontSize:13,fontWeight:tagTypeFilter===v?700:400,fontFamily:"inherit",transition:"all 0.2s"}}>{l}</button>
        ))}
      </div>
      <div data-tour="tag-manage-list">
      {(()=>{ const filtered=tagHierarchy.filter(cat=>tagTypeFilter==="income"?cat.type==="income":cat.type!=="income").filter(cat=>cat.tags.length>0); return filtered.length===0&&<div style={{textAlign:"center",padding:"60px 0",color:T.textDim,fontSize:14}}>此類別暫無標籤</div>; })()}
      {tagHierarchy.filter(cat=>tagTypeFilter==="income"?cat.type==="income":cat.type!=="income").filter(cat=>cat.tags.length>0).map(cat=>{
        const visibleTags = cat.tags;
        return (
        <div key={cat.name} style={{marginBottom:8}}>
          <button onClick={()=>toggleExpand(cat.name)} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"14px 16px",background:T.card,borderRadius:expandedCategories[cat.name]?"12px 12px 0 0":"12px",border:`1px solid ${T.border}`,borderBottom:expandedCategories[cat.name]?`1px solid ${T.border}`:"none",cursor:"pointer",textAlign:"left",fontFamily:"inherit"}}>
            <div style={{width:40,height:40,borderRadius:12,background:cat.color+"22",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>{cat.icon}</div>
            <div style={{flex:1}}>
              <div style={{fontWeight:700,fontSize:15,color:T.text}}>{cat.name}</div>
              <div style={{fontSize:12,color:T.muted,marginTop:2}}>
                {visibleTags.length} 個標籤 · {cat.count} 筆                {cat.expense>0&&<span style={{color:"#F87171"}}> · -${cat.expense.toLocaleString()}</span>}
              </div>
            </div>
            <span style={{color:T.muted,fontSize:12}}>{expandedCategories[cat.name]?"▾":"▸"}</span>
          </button>
          {expandedCategories[cat.name]&&(
            <div style={{background:T.sub,border:`1px solid ${T.border}`,borderTop:"none",borderRadius:"0 0 12px 12px",overflow:"hidden"}}>
              {visibleTags.length===0&&<div style={{padding:"16px",fontSize:13,color:T.textDim,textAlign:"center"}}>此類別沒有標籤</div>}
              {visibleTags.map(tag=>(
                <div key={tag.name} className="pot-tag-row" onClick={()=>{setActiveTag(tag.name);setTagCatFilter("全部");}} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 16px",borderBottom:`1px solid ${T.border}`}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:tagColor(tag.name,allTags),flexShrink:0}}/>
                  <span style={{flex:1,color:T.text,fontSize:14,fontWeight:500}}>#{tag.name}</span>
                  <div style={{textAlign:"right",fontSize:12}}>
                    <div style={{color:T.muted}}>{tag.count} 筆</div>
                    {tag.total>0&&<div style={{color:"#F87171"}}>-${tag.total.toLocaleString()}</div>}
                    {tag.income>0&&<div style={{color:"#34D399"}}>+${tag.income.toLocaleString()}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        );
      })}
      </div>
      </div>
    </div>
  );

  // ===== Recurring Tab =====
  const recurringExpense = recurringWithDays.filter(r=>(r.type||"expense")!=="income");
  const recurringIncome = recurringWithDays.filter(r=>r.type==="income");
  const totalRecurringExpense = recurringExpense.reduce((s,r)=>s+r.amount,0);
  const totalRecurringIncome = recurringIncome.reduce((s,r)=>s+r.amount,0);

  const renderRecurringItem = (r) => {
    const cat=allCategories.find(c=>c.name===r.category);
    const isIncome=r.type==="income";
    const dl=r.daysLeft===0?"今天":r.daysLeft===1?"明天":`${r.daysLeft} 天後`;
    return (
      <div key={r.id} style={{background:T.card,borderRadius:14,padding:"16px",marginBottom:10,border:`1px solid ${T.border}`}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div style={{width:44,height:44,borderRadius:12,background:(cat?.color||"#94A3B8")+"22",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>{cat?.icon||"💰"}</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontWeight:700,fontSize:15,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.name}</div>
            <div style={{fontSize:12,color:T.muted,marginTop:2}}>{r.category} · 每月 {r.day} 號{isIncome?"入帳":"扣款"}</div>
          </div>
          <div style={{textAlign:"right",flexShrink:0}}>
            <div style={{fontSize:18,fontWeight:700,color:isIncome?"#34D399":"#F87171"}}>{isIncome?"+":"-"}${(r.amount||0).toLocaleString()}</div>
            <div style={{fontSize:12,color:r.daysLeft<=3?"#FBBF24":T.muted,marginTop:2}}>{dl}</div>
          </div>
        </div>
        {(r.tags?.length>0||r.note)&&(
          <div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${T.border}`}}>
            {r.tags?.length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:r.note?6:0}}>{r.tags.map(t=><span key={t} style={{fontSize:12,padding:"2px 8px",borderRadius:12,background:tagColor(t,allTags)+"22",color:tagColor(t,allTags)}}>#{t}</span>)}</div>}
            {r.note&&<div style={{fontSize:13,color:T.textDim}}>{r.note}</div>}
          </div>
        )}
        <div style={{display:"flex",gap:6,marginTop:12}}>
          <button onClick={()=>setEditRecurring(r)} style={{flex:1,padding:"8px",border:`1px solid ${T.border}`,borderRadius:8,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13,fontFamily:"inherit"}}>編輯</button>
          <button onClick={()=>setDeleteRecurring(r)} style={{flex:1,padding:"8px",border:"none",borderRadius:8,background:"#F8717122",color:"#F87171",cursor:"pointer",fontSize:13,fontFamily:"inherit"}}>刪除</button>
        </div>
      </div>
    );
  };

  const renderRecurringTab = () => (
    <div style={{padding:isDesktop?"24px":"16px"}}>
      <div data-tour="recurring-section">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div>
          <div style={{display:"flex",gap:12,marginTop:4}}>
            <span style={{fontSize:18,fontWeight:700,color:"#F87171"}}>支出 -${totalRecurringExpense.toLocaleString()}</span>
            <span style={{fontSize:18,fontWeight:700,color:"#34D399"}}>收入 +${totalRecurringIncome.toLocaleString()}</span>
          </div>
        </div>
        <button onClick={()=>setAddRecurring(true)} style={{background:"linear-gradient(135deg,#6366F1,#8B5CF6)",border:"none",borderRadius:10,padding:"10px 20px",color:"#fff",cursor:"pointer",fontSize:14,fontWeight:600,fontFamily:"inherit"}}>新增定期</button>
      </div>
      {recurringWithDays.length===0&&<div style={{textAlign:"center",padding:"60px 0",color:T.textDim,fontSize:14}}>尚未設定任何定期項目</div>}
      </div>
      {recurringExpense.length>0&&(
        <>
          <div style={{fontSize:11,color:T.muted,letterSpacing:2,marginBottom:8,paddingLeft:2}}>定期支出</div>
          {recurringExpense.map(renderRecurringItem)}
        </>
      )}
      {recurringIncome.length>0&&(
        <>
          <div style={{fontSize:11,color:T.muted,letterSpacing:2,marginBottom:8,paddingLeft:2,marginTop:recurringExpense.length>0?16:0}}>定期收入</div>
          {recurringIncome.map(renderRecurringItem)}
        </>
      )}
    </div>
  );

  const renderTab = () => tab==="add"?renderAddTab():tab==="list"?renderListTab():tab==="tags"?renderTagsTab():renderRecurringTab();

  return (
    <div style={{fontFamily:"'Noto Sans TC',sans-serif",background:T.bg,minHeight:"100vh",color:T.text,overflowX:"hidden"}}>
      <style>{".pot-tag-row{cursor:pointer;transition:background 0.15s}.pot-tag-row:hover{background:rgba(99,102,241,0.08)}"}</style>
      <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+TC:wght@300;400;500;700&family=DM+Mono&display=swap" rel="stylesheet"/>
      {showOnboarding&&(isDesktop?<SpotlightTour onComplete={completeOnboarding} setTab={setTab} onAction={handleTourAction} T={T}/>:<OnboardingWizard onComplete={completeOnboarding} T={T}/>)}
      {saveError&&<div onClick={()=>setSaveError("")} style={{position:"fixed",top:20,right:20,background:"#EF4444",color:"#fff",padding:"12px 16px",borderRadius:8,zIndex:9999,maxWidth:320,fontSize:13,cursor:"pointer",boxShadow:"0 4px 12px rgba(0,0,0,0.3)"}}>⚠ {saveError}</div>}
      {showCal&&<CalendarPicker value={form.date} onChange={v=>setForm(f=>({...f,date:v}))} onClose={()=>setShowCal(false)} T={T}/>}
      {showManageCat&&<ManageCategoriesModal categories={allCategories} onAdd={saveCategory} onUpdate={updateCategory} onDelete={deleteCategory} onReorder={reorderCategories} onClose={()=>setShowManageCat(false)} T={T} isDesktop={isDesktop}/>}
      {showManagePayment&&<PaymentMethodsModal methods={allPaymentMethods} onAdd={savePaymentMethod} onUpdate={updatePaymentMethod} onDelete={deletePaymentMethod} onReorder={reorderPayments} onClose={()=>setShowManagePayment(false)} T={T} isDesktop={isDesktop} isIncome={paymentModalIncome}/>}
      {(addRecurring||editRecurring)&&<RecurringModal record={editRecurring} allCategories={allCategories} tagDocs={tagDocs} onSave={saveRecurring} onClose={()=>{setAddRecurring(false);setEditRecurring(null);}} T={T}/>}
      {editRecord&&<RecordModal record={editRecord} allCategories={allCategories} tagDocs={tagDocs} allPaymentMethods={allPaymentMethods} onSave={saveRecord} onClose={()=>setEditRecord(null)} T={T}/>}
      {deleteTarget&&<ConfirmDialog message={`刪除 ${deleteTarget.category} $${deleteTarget.amount.toLocaleString()} 這筆記錄？`} onConfirm={()=>deleteRecord(deleteTarget)} onCancel={()=>setDeleteTarget(null)} th={T}/>}
      {deleteTagTarget&&<ConfirmDialog message={`刪除標籤 #${deleteTagTarget}？（相關記錄保留，標籤被移除）`} onConfirm={()=>deleteTag(deleteTagTarget)} onCancel={()=>setDeleteTagTarget(null)} th={T}/>}
      {deleteRecurring&&<ConfirmDialog message={`刪除 ${deleteRecurring.name}（${deleteRecurring.type==="income"?"定期收入":"定期支出"}）？`} onConfirm={()=>delRecurring(deleteRecurring)} onCancel={()=>setDeleteRecurring(null)} th={T}/>}
      {TagDetailModal}
      {isDesktop ? (
        <div style={{display:"flex"}}>
          {Sidebar}
          <div style={{marginLeft:240,flex:1,minHeight:"100vh"}}>{renderTab()}</div>
        </div>
      ) : (
        <div style={{paddingTop:56}}>
          <div style={{position:"fixed",top:0,left:0,right:0,background:T.headerBg,borderBottom:`1px solid ${T.border}`,display:"flex",zIndex:11}}>
            {navItems.map(([v,icon,label])=>(
              <button key={v} onClick={()=>{setTab(v);setActiveTag(null);}} style={{flex:1,padding:"10px 0 8px",border:"none",background:"transparent",color:tab===v?"#818CF8":T.muted,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:2,fontFamily:"inherit"}}>
                <span style={{fontSize:20}}>{icon}</span>
                <span style={{fontSize:10}}>{label}</span>
              </button>
            ))}
          </div>
          {MobileHeader}
          {renderTab()}
        </div>
      )}
      {tab!=="add"&&showScrollTop&&(<button onClick={()=>window.scrollTo({top:0,behavior:"smooth"})} style={{position:"fixed",right:isDesktop?24:16,bottom:isDesktop?24:24,width:44,height:44,borderRadius:"50%",background:"#6366F1",border:"none",color:"#fff",cursor:"pointer",fontSize:20,display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,boxShadow:"0 4px 16px rgba(99,102,241,0.5)"}}>↑</button>)}
    </div>
  );
}

export default function App() {
  const [authState, setAuthState] = useState("loading");
  const [currentUser, setCurrentUser] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  useEffect(()=>{ getRedirectResult(auth).catch(()=>{}); return onAuthStateChanged(auth,user=>{ if(!user){setAuthState("login");return;} if(ALLOWED_EMAILS.includes(user.email)){setCurrentUser(user);setAuthState("authed");}else{setCurrentUser(user);setAuthState("unauthorized");} }); },[]);
  const isMobile=/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const handleLogin=async()=>{ setLoginLoading(true);setLoginError(""); try{ const provider=new GoogleAuthProvider(); if(isMobile){await signInWithRedirect(auth,provider);}else{await signInWithPopup(auth,provider);} }catch(e){setLoginError("登入失敗，請再試一次");} setLoginLoading(false); };
  const handleLogout = async()=>{ await signOut(auth);setAuthState("login");setCurrentUser(null); };
  if(authState==="loading")return <div style={{background:"#0F0F13",minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center"}}><div style={{color:"#64748B",fontSize:14}}>載入中.....</div></div>;
  if(authState==="login")return <LoginScreen onLogin={handleLogin} loading={loginLoading} error={loginError}/>;
  if(authState==="unauthorized")return <UnauthorizedScreen email={currentUser?.email} onLogout={handleLogout}/>;
  return <BudgetApp user={currentUser} onLogout={handleLogout}/>;
}
